import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight, Plus, X, Trash2, PenLine, Calendar as CalendarIcon,
  CheckCircle2, DollarSign, Clapperboard, Handshake, WalletCards,
  Library, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Copy, Check,
  Search, Filter, Clock, PlayCircle, Video, Film, Sparkles, Layers,
  TrendingUp, TrendingDown, ArrowDownRight, FileText, Share2, Palette, Tag,
  Users, Shield, ShieldCheck, UserCheck, Key, Lock, CheckSquare, Square, Edit2,
  AlignLeft, Send, Link2, MessageSquare, Download, Paperclip, UploadCloud
} from "lucide-react";

const RAW_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = (RAW_URL && !RAW_URL.includes("contenthq-backend")) ? RAW_URL : "http://localhost:8000";
const API = `${BASE_URL.replace(/\/$/, "")}/api`;

export const roleLabel = { admin: "Admin / Owner", manager: "Manager", script_writer: "Script Writer", editor: "Editor" };
export const api = (token) => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

export const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
export const moneyCompact = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const trim = (x) => x.replace(/\.0$/, "");
  if (abs >= 1e7) return `${sign}₹${trim((abs / 1e7).toFixed(1))}Cr`;
  if (abs >= 1e5) return `${sign}₹${trim((abs / 1e5).toFixed(1))}L`;
  if (abs >= 1e3) return `${sign}₹${trim((abs / 1e3).toFixed(1))}K`;
  return `${sign}₹${abs.toLocaleString("en-IN")}`;
};

export function ErrorBox({ message }) {
  return (
    <div className="error-card" style={{ padding: "18px 24px", background: "#fef2f2", border: "1px solid #fee2e2", borderRadius: "8px", color: "#b91c1c", margin: "20px 0" }}>
      <b>Notice:</b> {message}
    </div>
  );
}

/* ==========================================================================
   1. DASHBOARD (# Dashboard)
   ========================================================================== */
export function Dashboard({ user, token }) {
  const [data, setData] = useState({ counts: {}, upcoming_queue: [], recent_txs: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api(token).get("/dashboard")
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Unable to load dashboard"))
      .finally(() => setLoading(false));
  }, [token]);

  if (err) return <ErrorBox message={err} />;

  const counts = data.counts || {};
  const profitLoss = Number(counts.profit_loss || 0);
  const isProfit = profitLoss >= 0;

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">WORKSPACE OVERVIEW · {roleLabel[user.role]?.toUpperCase() || "ADMIN"}</p>
          <h1 data-testid="page-title">Dashboard</h1>
          <p className="muted">Total content uploaded, scheduled queue, expenses, revenue, and studio velocity.</p>
        </div>
      </div>

      {/* Top Core Metrics */}
      <div className="dashboard-metric-grid">
        <div className="dash-card">
          <div className="dash-card-head">
            <span className="dash-icon blue"><Film size={18} /></span>
            <small>Content Uploaded</small>
          </div>
          <strong className="dash-num">{counts.uploaded_month || 0}</strong>
          <div className="dash-sub-pills">
            <span className="mini-pill">Total: <b>{counts.uploaded_total || 0}</b></span>
            <span className="mini-pill">This week: <b>{counts.uploaded_week || 0}</b></span>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <span className="dash-icon purple"><CalendarIcon size={18} /></span>
            <small>Content Scheduled</small>
          </div>
          <strong className="dash-num">{counts.scheduled_month || 0}</strong>
          <div className="dash-sub-pills">
            <span className="mini-pill">Total: <b>{counts.scheduled_total || 0}</b></span>
            <span className="mini-pill">This week: <b>{counts.scheduled_week || 0}</b></span>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <span className="dash-icon green"><TrendingUp size={18} /></span>
            <small>Revenue</small>
          </div>
          <strong className="dash-num green-text">{money(counts.credit_this_month)}</strong>
          <div className="dash-sub-pills">
            <span className="mini-pill">Total: <b>{money(counts.total_credit)}</b></span>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <span className="dash-icon red"><TrendingDown size={18} /></span>
            <small>Expenses</small>
          </div>
          <strong className="dash-num red-text">{money(counts.debit_this_month)}</strong>
          <div className="dash-sub-pills">
            <span className="mini-pill">Total: <b>{money(counts.total_debit)}</b></span>
          </div>
        </div>
      </div>

      {/* Overall Profit/Loss Callout Banner */}
      <div className={`pnl-summary-banner ${isProfit ? "profit" : "loss"}`}>
        <div className="pnl-banner-left">
          <span className="pnl-tag">{isProfit ? "OVERALL NET PROFIT" : "OVERALL NET LOSS"}</span>
          <h2>{money(profitLoss)}</h2>
          <p>Profit/Loss = Total Credit ({money(counts.total_credit)}) − Total Expenses ({money(counts.total_debit)})</p>
        </div>
        <div className="pnl-banner-right">
          <button className="banner-action-btn" onClick={() => navigate("/finances")}>
            View Finances & Ledger <ArrowUpRight size={16} />
          </button>
        </div>
      </div>

      {/* Interactive Operational Modules Grid */}
      <div className="dashboard-sections-grid">
        {/* Production Flow */}
        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">STUDIO PIPELINE</p>
              <h2>Production in Flight</h2>
            </div>
            <button className="text-btn" onClick={() => navigate("/production")}>
              Open pipeline <ArrowUpRight size={15} />
            </button>
          </div>
          <div className="dash-pipeline-steps">
            <div className="pipeline-step-item" onClick={() => navigate("/production")}>
              <span className="step-count">{counts.prod_completed_scripts || 0}</span>
              <span className="step-name">Completed Scripts</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="pipeline-step-item" onClick={() => navigate("/production")}>
              <span className="step-count">{counts.prod_recorded || 0}</span>
              <span className="step-name">Recorded</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="pipeline-step-item" onClick={() => navigate("/production")}>
              <span className="step-count">{counts.prod_editing || 0}</span>
              <span className="step-name">Editing</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="pipeline-step-item" onClick={() => navigate("/production")}>
              <span className="step-count">{counts.prod_edited || 0}</span>
              <span className="step-name">Edited</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="pipeline-step-item uploaded" onClick={() => navigate("/production")}>
              <span className="step-count">{counts.prod_uploaded || 0}</span>
              <span className="step-name">Uploaded</span>
            </div>
          </div>
        </section>

        {/* Content Calendar upcoming */}
        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">SCHEDULE & QUEUE</p>
              <h2>Upcoming Content</h2>
            </div>
            <button className="text-btn" onClick={() => navigate("/calendar")}>
              Open calendar <ArrowUpRight size={15} />
            </button>
          </div>
          <div className="dash-queue-list">
            {(data.upcoming_queue || []).slice(0, 4).map((item, idx) => (
              <div key={idx} className="queue-row" onClick={() => navigate("/calendar")}>
                <span className="queue-platform-pill">{item.platform || "Video"}</span>
                <div className="queue-info">
                  <b>{item.title}</b>
                  <small>Target: {item.upload_date || item.due_date || "Coming soon"}</small>
                </div>
                <span className="status-pill info">{item.status}</span>
                <ArrowUpRight size={15} className="row-arrow" />
              </div>
            ))}
            {(!data.upcoming_queue || data.upcoming_queue.length === 0) && (
              <div className="empty-mini">No upcoming releases queued. Click to schedule in Calendar.</div>
            )}
          </div>
        </section>
      </div>

      <div className="dashboard-sections-grid mt-16">
        {/* Scripts Workspace snapshot */}
        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">WRITING ROOM</p>
              <h2>Scripts Pipeline</h2>
            </div>
            <button className="text-btn" onClick={() => navigate("/scripts")}>
              Write scripts <ArrowUpRight size={15} />
            </button>
          </div>
          <div className="scripts-split-bar">
            <div className="script-stat-block" onClick={() => navigate("/scripts")}>
              <small>New / Drafts</small>
              <strong>{counts.scripts_new || 0}</strong>
              <span>Workspace queue</span>
            </div>
            <div className="script-stat-block" onClick={() => navigate("/scripts")}>
              <small>Completed</small>
              <strong className="blue-text">{counts.scripts_completed || 0}</strong>
              <span>Ready for shoot</span>
            </div>
            <div className="script-stat-block" onClick={() => navigate("/scripts")}>
              <small>Uploaded</small>
              <strong className="green-text">{counts.scripts_uploaded || 0}</strong>
              <span>Published archives</span>
            </div>
          </div>
        </section>

        {/* Brand Deals snapshot */}
        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">SPONSORSHIPS</p>
              <h2>Brand Deals</h2>
            </div>
            <button className="text-btn" onClick={() => navigate("/deals")}>
              View deals <ArrowUpRight size={15} />
            </button>
          </div>
          <div className="deals-mini-grid">
            <div className="deal-mini-box">
              <small>Leads</small>
              <b>{counts.deals_leads || 0}</b>
            </div>
            <div className="deal-mini-box">
              <small>Locked</small>
              <b>{counts.deals_locked || 0}</b>
            </div>
            <div className="deal-mini-box">
              <small>Uploaded</small>
              <b>{counts.deals_uploaded || 0}</b>
            </div>
            <div className="deal-mini-box highlight">
              <small>Paid</small>
              <b>{counts.deals_paid || 0}</b>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ==========================================================================
   2. SCRIPTS WORKSPACE (New -> Completed -> Uploaded + Dedicated Script Editor)
   ========================================================================== */
export function ScriptsView({ user, token }) {
  const [scripts, setScripts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]); // array of active tag names
  const [tab, setTab] = useState("all"); // all | New | Completed | Uploaded
  const [search, setSearch] = useState("");
  const [activeScript, setActiveScript] = useState(null); // for editor modal
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copiedToast, setCopiedToast] = useState(false);
  const navigate = useNavigate();

  // Tag Manager Modal State (Admin)
  const [showTagManager, setShowTagManager] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ name: "", color: "#6366F1", description: "" });
  const [tagActionError, setTagActionError] = useState("");
  const [tagActionLoading, setTagActionLoading] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadScripts = async () => {
    try {
      setLoading(true);
      const r = await api(token).get("/resources/script");
      setScripts(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Unable to load scripts");
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const r = await api(token).get("/tags");
      setTags(r.data);
    } catch (e) {
      console.error("Unable to load tags:", e);
    }
  };

  useEffect(() => {
    loadScripts();
    loadTags();
  }, []);

  // Map tag name to tag object for rapid color lookup
  const tagMap = useMemo(() => {
    const map = {};
    for (const t of tags) {
      map[t.name] = t;
    }
    return map;
  }, [tags]);

  // Tag Management Handlers
  const handleSaveTag = async (e) => {
    e.preventDefault();
    setTagActionError("");
    const trimmedName = tagForm.name.trim();
    if (!trimmedName) {
      setTagActionError("Tag name is required");
      return;
    }

    setTagActionLoading(true);
    try {
      if (editingTag) {
        const res = await api(token).patch(`/tags/${editingTag.id}`, {
          name: trimmedName,
          color: tagForm.color,
          description: tagForm.description.trim()
        });
        const updated = res.data;
        setTags((prev) => prev.map((t) => (t.id === editingTag.id ? updated : t)));

        // If tag was renamed, update locally in all scripts & active filters
        if (editingTag.name !== updated.name) {
          setScripts((prev) =>
            prev.map((s) => ({
              ...s,
              tags: (s.tags || []).map((tn) => (tn === editingTag.name ? updated.name : tn))
            }))
          );
          setSelectedTags((prev) =>
            prev.map((tn) => (tn === editingTag.name ? updated.name : tn))
          );
          if (activeScript) {
            setActiveScript((prev) => ({
              ...prev,
              tags: (prev.tags || []).map((tn) => (tn === editingTag.name ? updated.name : tn))
            }));
          }
        }
        setEditingTag(null);
      } else {
        const res = await api(token).post("/tags", {
          name: trimmedName,
          color: tagForm.color,
          description: tagForm.description.trim()
        });
        setTags((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setTagForm({ name: "", color: "#6366F1", description: "" });
    } catch (err) {
      setTagActionError(err.response?.data?.detail || "Error saving tag");
    } finally {
      setTagActionLoading(false);
    }
  };

  const handleStartEditTag = (t) => {
    setEditingTag(t);
    setTagForm({
      name: t.name,
      color: t.color || "#6366F1",
      description: t.description || ""
    });
    setTagActionError("");
  };

  const handleCancelEditTag = () => {
    setEditingTag(null);
    setTagForm({ name: "", color: "#6366F1", description: "" });
    setTagActionError("");
  };

  const handleDeleteTag = async (tagToDelete) => {
    if (!window.confirm(`Delete tag "${tagToDelete.name}"? It will also be removed from any assigned scripts.`)) {
      return;
    }
    try {
      await api(token).delete(`/tags/${tagToDelete.id}`);
      setTags((prev) => prev.filter((t) => t.id !== tagToDelete.id));
      // Remove from local scripts
      setScripts((prev) =>
        prev.map((s) => ({
          ...s,
          tags: (s.tags || []).filter((tn) => tn !== tagToDelete.name)
        }))
      );
      setSelectedTags((prev) => prev.filter((tn) => tn !== tagToDelete.name));
      if (activeScript) {
        setActiveScript((prev) => ({
          ...prev,
          tags: (prev.tags || []).filter((tn) => tn !== tagToDelete.name)
        }));
      }
      if (editingTag?.id === tagToDelete.id) {
        handleCancelEditTag();
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete tag");
    }
  };

  // Filter scripts by tab, search, and one or more selected tags
  const filtered = useMemo(() => {
    return scripts.filter((s) => {
      const st = s.status || "New";
      const matchesTab =
        tab === "all" ? true :
          tab === "New" ? (!["Completed", "Uploaded"].includes(st)) :
            st.toLowerCase() === tab.toLowerCase();

      const q = search.toLowerCase();
      const scriptTags = Array.isArray(s.tags) ? s.tags : [];
      const matchesSearch =
        !q ||
        (s.title || "").toLowerCase().includes(q) ||
        (s.brief || "").toLowerCase().includes(q) ||
        (s.content || "").toLowerCase().includes(q) ||
        scriptTags.some((t) => t.toLowerCase().includes(q));

      // Multi-tag filtering: if tags are selected, script must match any of the selected tags
      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.some((selected) => scriptTags.includes(selected));

      return matchesTab && matchesSearch && matchesTags;
    });
  }, [scripts, tab, search, selectedTags]);

  const updateStage = async (script, newStage) => {
    try {
      await api(token).patch(`/resources/script/${script.id}`, { status: newStage });
      setScripts((prev) => prev.map((s) => s.id === script.id ? { ...s, status: newStage } : s));
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update stage");
    }
  };

  const deleteScript = async (id) => {
    if (!window.confirm("Are you sure you want to delete this script?")) return;
    try {
      await api(token).delete(`/resources/script/${id}`);
      setScripts((prev) => prev.filter((s) => s.id !== id));
      if (activeScript?.id === id) setShowEditor(false);
      // Refresh tags usage counts
      loadTags();
    } catch (e) {
      alert("Failed to delete script");
    }
  };

  const openNewScript = () => {
    setActiveScript({
      id: "",
      title: "",
      genre: "",
      status: "New",
      target_duration: "3-5 mins",
      hook: "",
      content: "",
      cta: "",
      references: [],
      notes: "",
      tags: []
    });
    setShowEditor(true);
  };

  const openEditScript = (s) => {
    setActiveScript({
      id: s.id,
      title: s.title || "",
      genre: s.genre || "",
      status: s.status || "New",
      target_duration: s.target_duration || "3-5 mins",
      hook: typeof s.hook === "string" ? s.hook : "",
      content: typeof s.content === "string" ? s.content : (typeof s.brief === "string" ? s.brief : ""),
      cta: typeof s.cta === "string" ? s.cta : "",
      references: Array.isArray(s.references) ? s.references : [],
      notes: typeof s.notes === "string" ? s.notes : "",
      tags: Array.isArray(s.tags)
        ? s.tags.map((t) => (typeof t === "object" && t !== null ? t.name || t.title || "" : String(t || ""))).filter(Boolean)
        : []
    });
    setShowEditor(true);
  };

  const toggleTagOnActiveScript = (tagName) => {
    setActiveScript((prev) => {
      const current = prev.tags || [];
      const isAssigned = current.includes(tagName);
      const next = isAssigned
        ? current.filter((tn) => tn !== tagName)
        : [...current, tagName];
      return { ...prev, tags: next };
    });
  };

  const saveScript = async (e) => {
    e.preventDefault();
    if (!activeScript.title.trim()) return;
    try {
      if (activeScript.id) {
        const r = await api(token).patch(`/resources/script/${activeScript.id}`, activeScript);
        setScripts((prev) => prev.map((s) => (s.id === activeScript.id ? r.data : s)));
      } else {
        const r = await api(token).post("/resources/script", activeScript);
        setScripts((prev) => [r.data, ...prev]);
      }
      setShowEditor(false);
      loadTags(); // refresh usage counts
    } catch (err) {
      alert(err.response?.data?.detail || "Error saving script");
    }
  };

  const copyScriptText = () => {
    if (!activeScript) return;
    const tagsLine = activeScript.tags?.length ? `TAGS: ${activeScript.tags.join(", ")}\n\n` : "";
    const text = `TITLE: ${activeScript.title}\n${tagsLine}[HOOK]\n${activeScript.hook}\n\n[MAIN SCRIPT]\n${activeScript.content}\n\n[CALL TO ACTION]\n${activeScript.cta}`;
    navigator.clipboard.writeText(text);
    setCopiedToast(true);
    setTimeout(() => setCopiedToast(false), 2000);
  };

  const wordCount = (text = "") => {
    if (typeof text !== "string") text = text ? String(text) : "";
    return text.trim().split(/\s+/).filter(Boolean).length;
  };
  const estReadTime = (wc) => {
    const min = Math.ceil(wc / 130);
    return `${min} min read (~${wc} words)`;
  };

  const PRESET_COLORS = [
    { name: "Indigo", hex: "#6366F1" },
    { name: "Emerald", hex: "#10B981" },
    { name: "Amber", hex: "#F59E0B" },
    { name: "Rose", hex: "#EC4899" },
    { name: "Violet", hex: "#8B5CF6" },
    { name: "Cyan", hex: "#06B6D4" },
    { name: "Ruby", hex: "#EF4444" },
    { name: "Slate", hex: "#64748B" }
  ];

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">CREATIVE WORKSPACE</p>
          <h1>Scripts</h1>
          <p className="muted">Write new scripts, organize drafts with custom tags, and review pipeline progress.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {isAdmin && (
            <button
              type="button"
              className="secondary-btn compact"
              data-testid="manage-tags-button"
              onClick={() => { setShowTagManager(true); setTagActionError(""); }}
            >
              <Tag size={15} /> Manage Tags ({tags.length})
            </button>
          )}
          <button className="primary-btn compact" data-testid="write-script-button" onClick={openNewScript}>
            <Plus size={16} /> Write Script
          </button>
        </div>
      </div>

      {/* Tabs & Search Controls */}
      <div className="scripts-controls-row">
        <div className="tab-pill-group">
          <button className={tab === "all" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("all")}>
            All ({scripts.length})
          </button>
          <button className={tab === "New" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("New")}>
            New / Writing ({scripts.filter(s => !["Completed", "Uploaded"].includes(s.status)).length})
          </button>
          <button className={tab === "Completed" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("Completed")}>
            Completed ({scripts.filter(s => s.status === "Completed").length})
          </button>
          <button className={tab === "Uploaded" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("Uploaded")}>
            Uploaded ({scripts.filter(s => s.status === "Uploaded").length})
          </button>
        </div>

        <div className="search-wrap">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search scripts by title, content, or tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tag Filter Bar (One or More Tags) */}
      <div className="scripts-tag-filter-bar" data-testid="scripts-tag-filter-bar">
        <div className="tag-filter-label">
          <Filter size={13} />
          <span>Tags:</span>
        </div>
        <div className="tag-filter-chips">
          <button
            type="button"
            data-testid="filter-tag-all"
            className={`tag-filter-chip ${selectedTags.length === 0 ? "active" : ""}`}
            onClick={() => setSelectedTags([])}
          >
            All Tags
          </button>
          {tags.map((t) => {
            const isSelected = selectedTags.includes(t.name);
            const count = scripts.filter((s) => (s.tags || []).includes(t.name)).length;
            const color = t.color || "#6366F1";
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`filter-tag-${t.name}`}
                className={`tag-filter-chip ${isSelected ? "active" : ""}`}
                onClick={() => {
                  if (isSelected) {
                    setSelectedTags(selectedTags.filter((tn) => tn !== t.name));
                  } else {
                    setSelectedTags([...selectedTags, t.name]);
                  }
                }}
                style={
                  isSelected
                    ? {
                      backgroundColor: `${color}22`,
                      borderColor: color,
                      color: color,
                    }
                    : {}
                }
              >
                <span className="tag-chip-dot" style={{ backgroundColor: color }} />
                <span>{t.name}</span>
                <span className="tag-chip-count">({count})</span>
                {isSelected && <X size={12} className="tag-chip-remove" />}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button
              type="button"
              data-testid="clear-tag-filters"
              className="clear-tags-btn"
              onClick={() => setSelectedTags([])}
            >
              Clear filters ({selectedTags.length})
            </button>
          )}
        </div>
      </div>

      {/* Scripts Grid */}
      <div className="scripts-cards-grid">
        {filtered.map((s) => {
          const st = s.status || "New";
          const wc = wordCount((s.content || "") + " " + (s.brief || ""));
          const scriptTags = Array.isArray(s.tags) ? s.tags : [];
          return (
            <div key={s.id} className="script-card" data-testid={`script-card-${s.id}`}>
              <div className="script-card-top">
                <span className="script-genre">{s.genre || "Script"}</span>
                <span className={`status-pill ${st === "Completed" ? "good" : st === "Uploaded" ? "busy" : "soft"}`}>
                  {st}
                </span>
              </div>

              {/* Assigned Tags Display on Script Card Listing */}
              {scriptTags.length > 0 && (
                <div className="script-card-tags" data-testid={`script-tags-${s.id}`}>
                  {scriptTags.map((tagName) => {
                    const tagObj = tagMap[tagName];
                    const color = tagObj?.color || "#6366F1";
                    return (
                      <span
                        key={tagName}
                        className="script-tag-badge"
                        style={{
                          backgroundColor: `${color}18`,
                          color: color,
                          borderColor: `${color}40`,
                        }}
                      >
                        <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                        {tagName}
                      </span>
                    );
                  })}
                </div>
              )}

              <h3 onClick={() => openEditScript(s)}>{s.title}</h3>
              <p className="script-snippet">
                {s.hook || s.content || s.brief || "Click to open writer and start drafting script…"}
              </p>
              <div className="script-meta">
                <span className="meta-words"><Clock size={13} /> {estReadTime(wc)}</span>
              </div>
              <div className="script-card-actions">
                <button type="button" className="card-action-btn edit" onClick={() => openEditScript(s)}>
                  <PenLine size={13} /> Open Writer
                </button>
                {st !== "Completed" && (
                  <button
                    type="button"
                    className="card-action-btn move"
                    onClick={() => updateStage(s, "Completed")}
                    title="Send script to Production pipeline (Completed Scripts)"
                  >
                    Move to Completed →
                  </button>
                )}
                {st === "Completed" && (
                  <button
                    type="button"
                    className="card-action-btn move-up"
                    onClick={() => updateStage(s, "Uploaded")}
                    title="Move to Uploaded in Production pipeline"
                  >
                    Move to Uploaded →
                  </button>
                )}
                {st === "Uploaded" && (
                  <button
                    type="button"
                    className="card-action-btn move-back"
                    onClick={() => updateStage(s, "Completed")}
                    title="Move back to Completed in Production"
                  >
                    ← Back to Completed
                  </button>
                )}
                {(st === "Completed" || st === "Uploaded") && (
                  <button
                    type="button"
                    className="card-action-btn view-prod"
                    onClick={() => navigate("/production")}
                    title="View this card in the Production pipeline"
                  >
                    <Clapperboard size={12} /> Production
                  </button>
                )}
                <button
                  type="button"
                  className="card-action-btn icon delete"
                  onClick={() => deleteScript(s.id)}
                  title="Delete script"
                  aria-label="Delete script"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <PenLine size={32} />
          <h3>No scripts found</h3>
          <p>
            {selectedTags.length > 0
              ? `No scripts match the selected tag filters (${selectedTags.join(", ")}). Click "Clear filters" to view all scripts.`
              : 'Click "Write Script" above to draft your first script in this workspace.'}
          </p>
          {selectedTags.length > 0 && (
            <button
              className="secondary-btn compact"
              style={{ marginTop: "12px" }}
              onClick={() => setSelectedTags([])}
            >
              Clear tag filters
            </button>
          )}
        </div>
      )}

      {/* Dedicated Script Writer / Details Modal */}
      {showEditor && activeScript && (
        <div className="modal-backdrop" onClick={() => setShowEditor(false)}>
          <div className="modal writer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="writer-header">
              <div>
                <p className="eyebrow">{activeScript.id ? "EDITING SCRIPT" : "NEW SCRIPT DRAFT"}</p>
                <h2>{activeScript.title || "Untitled Script"}</h2>

                {/* Display assigned tags on Script Details Header */}
                {activeScript.tags && activeScript.tags.length > 0 && (
                  <div className="writer-tags-preview" data-testid="writer-tags-preview" style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                    {activeScript.tags.map((rawTag) => {
                      const tagName = typeof rawTag === "object" && rawTag !== null ? rawTag.name || rawTag.title || "" : String(rawTag || "");
                      if (!tagName) return null;
                      const tagObj = tagMap[tagName];
                      const color = tagObj?.color || "#6366F1";
                      return (
                        <span
                          key={tagName}
                          className="script-tag-badge"
                          style={{
                            backgroundColor: `${color}22`,
                            color: color,
                            borderColor: `${color}55`,
                            fontSize: "12px",
                            padding: "3px 9px",
                          }}
                        >
                          <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="writer-header-actions">
                <button className="icon-text-btn" type="button" onClick={copyScriptText}>
                  {copiedToast ? <Check size={14} className="green-text" /> : <Copy size={14} />}
                  {copiedToast ? "Copied!" : "Copy Full Script"}
                </button>
                <button className="icon-btn" onClick={() => setShowEditor(false)}><X size={18} /></button>
              </div>
            </div>

            <form onSubmit={saveScript} className="writer-form">
              <div className="writer-meta-row">
                <div className="form-group flex-2">
                  <label>Script Title</label>
                  <input
                    type="text"
                    required
                    data-testid="script-title-input"
                    placeholder="e.g. 5 AI Coding Workflows That Save 10 Hours a Week"
                    value={activeScript.title}
                    onChange={(e) => setActiveScript({ ...activeScript, title: e.target.value })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Stage</label>
                  <select
                    value={activeScript.status}
                    onChange={(e) => setActiveScript({ ...activeScript, status: e.target.value })}
                  >
                    <option value="New">New / In Draft</option>
                    <option value="Completed">Completed (Ready for shoot)</option>
                    <option value="Uploaded">Uploaded / Published</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Category / Platform</label>
                  <input
                    type="text"
                    placeholder="e.g. YouTube Long-form, Reel, Shorts"
                    value={activeScript.genre}
                    onChange={(e) => setActiveScript({ ...activeScript, genre: e.target.value })}
                  />
                </div>
              </div>

              {/* Tag Selector: Select one or more admin-created tags */}
              <div className="form-group" style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "7px" }}>
                  <label style={{ margin: 0, fontWeight: 700, fontSize: "12px", color: "#47546a" }}>
                    Script Tags <span style={{ color: "var(--muted)", fontWeight: "normal", fontSize: "12px" }}>({activeScript.tags?.length || 0} selected)</span>
                  </label>
                  {isAdmin && (
                    <button
                      type="button"
                      className="text-btn compact"
                      style={{ fontSize: "12px", color: "var(--blue)", display: "inline-flex", alignItems: "center", gap: "4px", padding: 0 }}
                      onClick={() => setShowTagManager(true)}
                    >
                      <Tag size={13} /> + Manage Tags
                    </button>
                  )}
                </div>

                <div className="script-tag-selector" data-testid="script-tag-selector">
                  {tags.map((t) => {
                    const isAssigned = (activeScript.tags || []).includes(t.name);
                    const color = t.color || "#6366F1";
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`select-tag-${t.name}`}
                        className={`tag-select-chip ${isAssigned ? "selected" : ""}`}
                        onClick={() => toggleTagOnActiveScript(t.name)}
                        style={
                          isAssigned
                            ? {
                              backgroundColor: `${color}25`,
                              borderColor: color,
                              color: color,
                            }
                            : {}
                        }
                      >
                        <span className="tag-chip-dot" style={{ backgroundColor: color }} />
                        <span>{t.name}</span>
                        {isAssigned ? (
                          <Check size={13} style={{ strokeWidth: 2.5 }} />
                        ) : (
                          <Plus size={13} style={{ opacity: 0.5 }} />
                        )}
                      </button>
                    );
                  })}
                  {tags.length === 0 && (
                    <p className="muted" style={{ fontSize: "12px", margin: 0 }}>
                      No tags created yet. {isAdmin ? 'Click "+ Manage Tags" to create your first tag.' : "An admin can create tags."}
                    </p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="label-with-hint">
                  <span>Visual Hook / First 3-5 Seconds</span>
                  <small>Capture attention immediately before the viewer scrolls</small>
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Stop using ChatGPT like a basic search engine. Here are 3 automation tricks nobody is talking about..."
                  value={activeScript.hook}
                  onChange={(e) => setActiveScript({ ...activeScript, hook: e.target.value })}
                />
              </div>

              <div className="form-group">
                <div className="script-body-head">
                  <label>Main Script Body & Talking Points</label>
                  <span className="stats-tag">
                    {wordCount(activeScript.content)} words · ~{Math.ceil(wordCount(activeScript.content) / 130)} mins
                  </span>
                </div>
                <textarea
                  rows={14}
                  className="writer-textarea"
                  placeholder="Draft your full scene breakdowns, cues, and talking points here..."
                  value={activeScript.content}
                  onChange={(e) => setActiveScript({ ...activeScript, content: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Call To Action (CTA) & Outro</label>
                <input
                  type="text"
                  placeholder="e.g. Drop a comment below if you want my custom workflow prompt file. Subscribe for next week's deep dive!"
                  value={activeScript.cta}
                  onChange={(e) => setActiveScript({ ...activeScript, cta: e.target.value })}
                />
              </div>

              <div className="writer-footer">
                <button type="button" className="text-btn" onClick={() => setShowEditor(false)}>
                  Cancel
                </button>
                <button type="submit" data-testid="save-script-button" className="primary-btn compact">
                  Save Script
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Tag Manager Modal (Create, Edit, Delete Tags) */}
      {showTagManager && (
        <div className="modal-backdrop" onClick={() => { setShowTagManager(false); handleCancelEditTag(); }}>
          <div className="modal tag-manager-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "620px" }}>
            <div className="modal-head">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Tag size={19} style={{ color: "var(--blue)" }} />
                  <h2 style={{ margin: 0, fontSize: "19px" }}>Manage Script Tags</h2>
                  <span className="badge-admin">Admin</span>
                </div>
                <p className="muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                  Create, edit, and organize tags for script categorization and filtering.
                </p>
              </div>
              <button
                className="icon-btn"
                data-testid="close-tag-manager"
                onClick={() => { setShowTagManager(false); handleCancelEditTag(); }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Create / Edit Tag Form */}
            <form onSubmit={handleSaveTag} className="tag-manager-form">
              <h3 style={{ fontSize: "14px", margin: "0 0 12px", color: editingTag ? "var(--blue)" : "var(--ink)" }}>
                {editingTag ? `Edit Tag: "${editingTag.name}"` : "Create New Custom Tag"}
              </h3>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: "12px" }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Tag Name *</label>
                  <input
                    type="text"
                    required
                    data-testid="tag-name-input"
                    placeholder="e.g. Series - BIB"
                    value={tagForm.name}
                    onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Badge Color</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="color"
                      data-testid="tag-color-picker"
                      value={tagForm.color}
                      onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                      style={{ width: "38px", height: "38px", padding: "2px", borderRadius: "6px", cursor: "pointer", border: "1px solid var(--line)" }}
                    />
                    <input
                      type="text"
                      data-testid="tag-color-hex"
                      value={tagForm.color}
                      onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                      style={{ fontSize: "12px", padding: "8px 6px" }}
                    />
                  </div>
                </div>
              </div>

              {/* Preset Palette Colors */}
              <div style={{ marginTop: "12px" }}>
                <span style={{ fontSize: "11px", color: "var(--muted)", fontWeight: 700, letterSpacing: "0.5px" }}>
                  COLOR PRESETS:
                </span>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  {PRESET_COLORS.map((c) => {
                    const isSelectedColor = tagForm.color.toLowerCase() === c.hex.toLowerCase();
                    return (
                      <button
                        key={c.hex}
                        type="button"
                        title={c.name}
                        onClick={() => setTagForm({ ...tagForm, color: c.hex })}
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: c.hex,
                          border: isSelectedColor ? "2px solid #0f172a" : "1px solid rgba(0,0,0,0.15)",
                          transform: isSelectedColor ? "scale(1.2)" : "scale(1)",
                          boxShadow: isSelectedColor ? "0 2px 6px rgba(0,0,0,0.2)" : "none",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: "12px", marginBottom: "12px" }}>
                <label>Description <small style={{ color: "var(--muted)", fontWeight: "normal" }}>(Optional)</small></label>
                <input
                  type="text"
                  data-testid="tag-description-input"
                  placeholder="e.g. Core episodic series or workflow classification"
                  value={tagForm.description}
                  onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                />
              </div>

              {tagActionError && (
                <div className="error" data-testid="tag-manager-error" style={{ margin: "10px 0" }}>
                  {tagActionError}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
                {editingTag && (
                  <button type="button" className="text-btn" onClick={handleCancelEditTag}>
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  data-testid="tag-submit-button"
                  className="primary-btn compact"
                  disabled={tagActionLoading}
                >
                  {tagActionLoading ? "Saving…" : (editingTag ? "Update Tag" : "Create Tag")}
                </button>
              </div>
            </form>

            {/* List of Existing Tags (Admin View, Edit, Delete) */}
            <div className="tag-manager-list" style={{ marginTop: "22px", borderTop: "1px solid var(--line)", paddingTop: "18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.6px" }}>
                  EXISTING TAGS ({tags.length})
                </h4>
              </div>

              {tags.length === 0 ? (
                <p className="muted" style={{ textAlign: "center", padding: "16px 0", fontSize: "13px" }}>
                  No tags created yet. Use the form above to add your first tag.
                </p>
              ) : (
                <div className="tags-table-wrap">
                  {tags.map((t) => (
                    <div key={t.id} className="tag-item-row" data-testid={`tag-item-${t.name}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                        <span
                          className="script-tag-badge"
                          style={{
                            backgroundColor: `${t.color || "#6366F1"}18`,
                            color: t.color || "#6366F1",
                            borderColor: `${t.color || "#6366F1"}40`,
                            fontSize: "12px",
                            padding: "4px 10px",
                          }}
                        >
                          <span className="tag-badge-dot" style={{ backgroundColor: t.color || "#6366F1" }} />
                          {t.name}
                        </span>
                        {t.description && (
                          <span className="tag-desc-text muted" title={t.description}>
                            {t.description}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span className="muted" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                          {t.usage_count === 1 ? "1 script" : `${t.usage_count || 0} scripts`}
                        </span>
                        <button
                          type="button"
                          data-testid={`edit-tag-btn-${t.name}`}
                          className="icon-btn-subtle"
                          onClick={() => handleStartEditTag(t)}
                          title={`Edit ${t.name}`}
                        >
                          <PenLine size={14} />
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-tag-btn-${t.name}`}
                          className="icon-btn-subtle danger"
                          onClick={() => handleDeleteTag(t)}
                          title={`Delete ${t.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   PIPELINE PERIOD FILTER HELPERS & COMPONENT
   ========================================================================== */
export function getWeekRange(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset * 7);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday as start of week
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  mon.setHours(0, 0, 0, 0);

  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
}

export function getMonthRange(offset = 0) {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const start = new Date(target.getFullYear(), target.getMonth(), 1, 0, 0, 0);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export function parseItemDate(item) {
  if (!item) return null;
  const s = item.upload_date || item.due_date || item.date || item.created_at;
  if (!s || s === "TBD") return null;
  if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}/.test(s)) {
    const parts = s.substring(0, 10).split("-");
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function PeriodFilterBar({
  periodMode,
  setPeriodMode,
  weekOffset,
  setWeekOffset,
  monthOffset,
  setMonthOffset,
  totalCount,
  filteredCount,
  itemLabel = "items",
  undatedCount = 0,
  includeUndated = false,
  setIncludeUndated
}) {
  const weekRange = useMemo(() => getWeekRange(weekOffset), [weekOffset]);
  const monthRange = useMemo(() => getMonthRange(monthOffset), [monthOffset]);

  const weekLabel = useMemo(() => {
    const { start, end } = weekRange;
    const sM = start.toLocaleDateString("en-US", { month: "short" });
    const eM = end.toLocaleDateString("en-US", { month: "short" });
    const sY = start.getFullYear();
    const eY = end.getFullYear();
    if (sM === eM && sY === eY) return `${sM} ${start.getDate()} – ${end.getDate()}, ${sY}`;
    if (sY === eY) return `${sM} ${start.getDate()} – ${eM} ${end.getDate()}, ${sY}`;
    return `${sM} ${start.getDate()}, ${sY} – ${eM} ${end.getDate()}, ${eY}`;
  }, [weekRange]);

  const monthLabel = useMemo(() => {
    return monthRange.start.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [monthRange]);

  return (
    <div className="pipeline-filter-bar">
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        {/* Segmented Control */}
        <div className="period-segmented-control">
          <button
            type="button"
            className={`period-seg-btn ${periodMode === "week" ? "active" : ""}`}
            onClick={() => { setPeriodMode("week"); setWeekOffset(0); }}
          >
            <CalendarIcon size={13} />
            <span>Weekly</span>
          </button>
          <button
            type="button"
            className={`period-seg-btn ${periodMode === "month" ? "active" : ""}`}
            onClick={() => { setPeriodMode("month"); setMonthOffset(0); }}
          >
            <Layers size={13} />
            <span>Monthly</span>
          </button>
          <button
            type="button"
            className={`period-seg-btn ${periodMode === "all" ? "active" : ""}`}
            onClick={() => setPeriodMode("all")}
          >
            <Clock size={13} />
            <span>All Time</span>
          </button>
        </div>

        {/* Date Navigator */}
        {periodMode === "week" && (
          <div className="period-nav-group">
            <button
              type="button"
              className="period-arrow-btn"
              onClick={() => setWeekOffset(w => w - 1)}
              title="Previous Week"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="period-label-pill">
              <CalendarIcon size={14} color="var(--blue)" />
              {weekLabel}
            </span>
            <button
              type="button"
              className="period-arrow-btn"
              onClick={() => setWeekOffset(w => w + 1)}
              title="Next Week"
            >
              <ChevronRight size={14} />
            </button>
            {weekOffset !== 0 && (
              <button
                type="button"
                className="period-today-btn"
                onClick={() => setWeekOffset(0)}
              >
                This Week
              </button>
            )}
          </div>
        )}

        {periodMode === "month" && (
          <div className="period-nav-group">
            <button
              type="button"
              className="period-arrow-btn"
              onClick={() => setMonthOffset(m => m - 1)}
              title="Previous Month"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="period-label-pill">
              <CalendarIcon size={14} color="var(--blue)" />
              {monthLabel}
            </span>
            <button
              type="button"
              className="period-arrow-btn"
              onClick={() => setMonthOffset(m => m + 1)}
              title="Next Month"
            >
              <ChevronRight size={14} />
            </button>
            {monthOffset !== 0 && (
              <button
                type="button"
                className="period-today-btn"
                onClick={() => setMonthOffset(0)}
              >
                This Month
              </button>
            )}
          </div>
        )}
      </div>

      {/* Meta Counter & Undated toggle */}
      <div className="period-summary-meta">
        <span className="period-count-badge">
          {filteredCount} {itemLabel}
        </span>
        {totalCount !== filteredCount && (
          <span style={{ fontSize: "11px", color: "#94a3b8" }}>
            ({totalCount} total)
          </span>
        )}
        {undatedCount > 0 && periodMode !== "all" && setIncludeUndated && (
          <button
            type="button"
            className="text-btn"
            style={{ fontSize: "11px", color: includeUndated ? "var(--blue)" : "#94a3b8", textDecoration: "underline" }}
            onClick={() => setIncludeUndated(!includeUndated)}
          >
            {includeUndated ? "Hide Undated" : `+${undatedCount} Undated`}
          </button>
        )}
        {periodMode !== "all" && totalCount > filteredCount && (
          <button
            type="button"
            className="text-btn"
            style={{ fontSize: "11px", color: "var(--blue)", padding: "2px 6px" }}
            onClick={() => setPeriodMode("all")}
          >
            View All
          </button>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   3. PRODUCTION (Completed Scripts -> Recorded -> Editing -> Edited -> Uploaded)
   ========================================================================== */
export function formatDateDisplay(dStr) {
  if (!dStr || dStr === "TBD") return "No date assigned";
  try {
    const d = new Date(dStr.includes("T") ? dStr : `${dStr}T00:00:00`);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return dStr;
  }
}

export function getStagePillClass(st) {
  switch (st) {
    case "Recorded": return "pill-recorded";
    case "Completed Scripts": return "pill-completed";
    case "Editing": return "pill-editing";
    case "Edited": return "pill-edited";
    case "Uploaded": return "pill-uploaded";
    default: return "pill-recorded";
  }
}

export function ProductionDetailModal({
  isOpen,
  onClose,
  item,
  user,
  token,
  teamMembers = [],
  tags = [],
  onSaveSuccess,
  onDeleteSuccess
}) {
  const [editingItem, setEditingItem] = useState(null);
  const [commentInput, setCommentInput] = useState("");
  const [editingPropKey, setEditingPropKey] = useState(null);
  const [showAddPropRow, setShowAddPropRow] = useState(false);
  const [newPropName, setNewPropName] = useState("");
  const [newPropVal, setNewPropVal] = useState("");
  const [tagPickerOpen, setTagPickerOpen] = useState(false);

  useEffect(() => {
    if (item) {
      const rawRef = item.references;
      const refStr = typeof rawRef === "string" ? rawRef : (Array.isArray(rawRef) ? rawRef.join(", ") : "");
      setEditingItem({
        ...item,
        title: item.title || "",
        status: item.status || "Completed Scripts",
        upload_date: item.upload_date || item.due_date || "",
        due_date: item.due_date || item.upload_date || "",
        assignee: item.assignee || "",
        assignee_id: item.assignee_id || "",
        arolls: typeof item.arolls === "string" ? item.arolls : (item.a_roll_url || ""),
        audio: typeof item.audio === "string" ? item.audio : "",
        brolls: typeof item.brolls === "string" ? item.brolls : (item.b_roll_url || ""),
        references: refStr,
        tags: Array.isArray(item.tags) ? item.tags : [],
        custom_properties: Array.isArray(item.custom_properties) ? item.custom_properties : [],
        content: typeof item.content === "string" ? item.content : (item.description || ""),
        description: typeof item.description === "string" ? item.description : "",
        comments: Array.isArray(item.comments) ? item.comments : []
      });
      setCommentInput("");
      setEditingPropKey(null);
      setShowAddPropRow(false);
      setTagPickerOpen(false);
    } else {
      setEditingItem(null);
    }
  }, [item]);

  if (!isOpen || !editingItem) return null;

  const STAGES = ["Completed Scripts", "Recorded", "Editing", "Edited", "Uploaded"];

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!editingItem.title.trim()) {
      alert("Please enter a title");
      return;
    }
    try {
      const formattedReferences = Array.isArray(editingItem.references)
        ? editingItem.references
        : (typeof editingItem.references === "string" && editingItem.references.trim()
          ? [editingItem.references.trim()]
          : []);

      const payload = {
        title: editingItem.title.trim(),
        status: editingItem.status || "Completed Scripts",
        platform: editingItem.platform || "YouTube",
        upload_date: editingItem.upload_date || "",
        due_date: editingItem.upload_date || editingItem.due_date || "",
        assignee: editingItem.assignee || "",
        assignee_id: editingItem.assignee_id || "",
        arolls: editingItem.arolls || "",
        audio: editingItem.audio || "",
        brolls: editingItem.brolls || "",
        references: formattedReferences,
        tags: Array.isArray(editingItem.tags) ? editingItem.tags : [],
        custom_properties: editingItem.custom_properties || [],
        content: editingItem.content || "",
        description: editingItem.content || editingItem.description || ""
      };

      let saved;
      if (editingItem.id) {
        const r = await api(token).patch(`/resources/content/${editingItem.id}`, payload);
        saved = r.data;
      } else {
        const r = await api(token).post("/resources/content", payload);
        saved = r.data;
      }
      if (onSaveSuccess) onSaveSuccess(saved);
      onClose();
    } catch (err) {
      console.error("Error saving production item:", err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
          : (err.message || "Error saving production item");
      alert(`Error saving production item: ${msg}`);
    }
  };

  const submitComment = async () => {
    if (!commentInput.trim() || !editingItem?.id) return;
    try {
      const r = await api(token).post(`/resources/content/${editingItem.id}/comments`, { text: commentInput.trim() });
      setEditingItem(prev => ({ ...prev, comments: r.data.comments || [] }));
      setCommentInput("");
    } catch (e) {
      alert("Failed to post comment");
    }
  };

  const handleAddCustomProp = () => {
    if (!newPropName.trim()) return;
    const updatedProps = [
      ...(editingItem.custom_properties || []),
      { key: newPropName.trim(), value: newPropVal.trim() }
    ];
    setEditingItem({ ...editingItem, custom_properties: updatedProps });
    setNewPropName("");
    setNewPropVal("");
    setShowAddPropRow(false);
  };

  const toggleTag = (tagName) => {
    const curTags = editingItem.tags || [];
    const next = curTags.includes(tagName)
      ? curTags.filter(t => t !== tagName)
      : [...curTags, tagName];
    setEditingItem({ ...editingItem, tags: next });
  };

  const handleDelete = async () => {
    if (!editingItem.id) {
      onClose();
      return;
    }
    if (!window.confirm("Delete this production item?")) return;
    try {
      await api(token).delete(`/resources/content/${editingItem.id}`);
      if (onDeleteSuccess) onDeleteSuccess(editingItem.id);
      onClose();
    } catch (e) {
      alert("Failed to delete item");
    }
  };

  return (
    <div className="notion-backdrop" onClick={onClose}>
      <div className="notion-modal" onClick={(e) => e.stopPropagation()}>
        {/* Top Bar: Title + Assignee Avatar + Close */}
        <div className="notion-top-bar">
          <div className="notion-title-row">
            <input
              required
              type="text"
              className="notion-title-input"
              placeholder="Untitled Item"
              value={editingItem.title}
              onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
            />
          </div>

          <div className="notion-top-actions">
            <div
              className="notion-avatar-badge"
              title={editingItem.assignee ? `Assigned to: ${editingItem.assignee}` : "Assign to teammate"}
            >
              {(editingItem.assignee || user?.name || "U")[0].toUpperCase()}
            </div>
            <button
              type="button"
              className="notion-close-btn"
              onClick={onClose}
              title="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Properties Table */}
        <div className="notion-properties-table">
          {/* 1. Status */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <Sparkles size={14} />
              <span>Status</span>
            </div>
            <div className="notion-prop-value">
              <span className={`notion-status-pill ${getStagePillClass(editingItem.status)}`}>
                ● {editingItem.status || "Recorded"}
              </span>
              <select
                className="notion-select-inline"
                value={editingItem.status}
                onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 2. Date with Clear Date Button (No compulsion to assign date) */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <CalendarIcon size={14} />
              <span>Date</span>
            </div>
            <div className="notion-prop-value" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span className="notion-date-text">
                {formatDateDisplay(editingItem.upload_date || editingItem.due_date)}
              </span>
              <input
                type="date"
                className="notion-select-inline"
                value={editingItem.upload_date || editingItem.due_date || ""}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  upload_date: e.target.value,
                  due_date: e.target.value
                })}
              />
              {(editingItem.upload_date || editingItem.due_date) && (
                <button
                  type="button"
                  className="notion-clear-date-btn"
                  onClick={() => setEditingItem({
                    ...editingItem,
                    upload_date: "",
                    due_date: ""
                  })}
                  title="Clear date (No compulsion to assign date)"
                >
                  <X size={12} /> Clear date
                </button>
              )}
            </div>
          </div>

          {/* 3. Tags Row */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <Tag size={14} />
              <span>Tags</span>
            </div>
            <div className="notion-prop-value" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
              {(editingItem.tags || []).map((tn) => {
                const tObj = tags.find(t => t.name === tn);
                const color = tObj?.color || "#6366F1";
                return (
                  <span
                    key={tn}
                    className="script-tag-badge"
                    style={{ backgroundColor: `${color}18`, color, borderColor: `${color}40`, cursor: "pointer" }}
                    onClick={() => toggleTag(tn)}
                    title="Click to remove tag"
                  >
                    <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                    {tn}
                    <span style={{ marginLeft: "4px", fontSize: "10px", opacity: 0.7 }}>✕</span>
                  </span>
                );
              })}
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  className="notion-add-tag-btn"
                  onClick={() => setTagPickerOpen(!tagPickerOpen)}
                >
                  <Plus size={11} /> Tag
                </button>
                {tagPickerOpen && (
                  <div className="notion-tag-picker-dropdown">
                    {tags.map((t) => {
                      const isAssigned = (editingItem.tags || []).includes(t.name);
                      return (
                        <div
                          key={t.id || t.name}
                          className={`notion-tag-picker-item ${isAssigned ? "active" : ""}`}
                          onClick={() => { toggleTag(t.name); setTagPickerOpen(false); }}
                        >
                          <span className="tag-badge-dot" style={{ backgroundColor: t.color || "#6366F1" }} />
                          <span>{t.name}</span>
                          {isAssigned && <Check size={12} />}
                        </div>
                      );
                    })}
                    {tags.length === 0 && <div className="notion-tag-picker-empty">No tags available</div>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 4. Assignee */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <Users size={14} />
              <span>Assignee</span>
            </div>
            <div className="notion-prop-value">
              {editingItem.assignee ? (
                <div className="notion-assignee-pill">
                  <span className="notion-assignee-avatar">
                    {(editingItem.assignee || "U")[0].toUpperCase()}
                  </span>
                  <span>{editingItem.assignee}</span>
                </div>
              ) : null}

              <select
                className="notion-assignee-select"
                value={editingItem.assignee_id || ""}
                onChange={(e) => {
                  const selId = e.target.value;
                  const member = teamMembers.find((m) => m.id === selId);
                  setEditingItem({
                    ...editingItem,
                    assignee_id: selId,
                    assignee: member ? member.name : ""
                  });
                }}
              >
                <option value="">+ Assign to teammate...</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.role_label || m.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 5. ARolls */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <AlignLeft size={14} />
              <span>ARolls</span>
            </div>
            <div className="notion-prop-value">
              {editingPropKey === "arolls" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                  <input
                    type="url"
                    className="notion-prop-input"
                    placeholder="Paste Drive / Dropbox link…"
                    value={editingItem.arolls || ""}
                    autoFocus
                    onChange={(e) => setEditingItem({ ...editingItem, arolls: e.target.value })}
                    onBlur={() => setEditingPropKey(null)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingPropKey(null)}
                  />
                  <button type="button" className="notion-select-inline" onClick={() => setEditingPropKey(null)}>Done</button>
                </div>
              ) : editingItem.arolls ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {typeof editingItem.arolls === "string" && editingItem.arolls.startsWith("http") ? (
                    <a href={editingItem.arolls} target="_blank" rel="noreferrer" className="notion-link-value">
                      {editingItem.arolls}
                    </a>
                  ) : (
                    <span className="notion-plain-text">{String(editingItem.arolls || "")}</span>
                  )}
                  <button
                    type="button"
                    className="notion-close-btn"
                    style={{ padding: "2px 4px" }}
                    onClick={() => setEditingPropKey("arolls")}
                    title="Edit ARolls link"
                  >
                    <PenLine size={12} />
                  </button>
                </div>
              ) : (
                <span className="notion-empty-placeholder" onClick={() => setEditingPropKey("arolls")}>
                  Empty
                </span>
              )}
            </div>
          </div>

          {/* 6. Audio */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <AlignLeft size={14} />
              <span>Audio</span>
            </div>
            <div className="notion-prop-value">
              {editingPropKey === "audio" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                  <input
                    type="url"
                    className="notion-prop-input"
                    placeholder="Paste audio asset link…"
                    value={editingItem.audio || ""}
                    autoFocus
                    onChange={(e) => setEditingItem({ ...editingItem, audio: e.target.value })}
                    onBlur={() => setEditingPropKey(null)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingPropKey(null)}
                  />
                  <button type="button" className="notion-select-inline" onClick={() => setEditingPropKey(null)}>Done</button>
                </div>
              ) : editingItem.audio ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {typeof editingItem.audio === "string" && editingItem.audio.startsWith("http") ? (
                    <a href={editingItem.audio} target="_blank" rel="noreferrer" className="notion-link-value">
                      {editingItem.audio}
                    </a>
                  ) : (
                    <span className="notion-plain-text">{String(editingItem.audio || "")}</span>
                  )}
                  <button
                    type="button"
                    className="notion-close-btn"
                    style={{ padding: "2px 4px" }}
                    onClick={() => setEditingPropKey("audio")}
                    title="Edit Audio link"
                  >
                    <PenLine size={12} />
                  </button>
                </div>
              ) : (
                <span className="notion-empty-placeholder" onClick={() => setEditingPropKey("audio")}>
                  Empty
                </span>
              )}
            </div>
          </div>

          {/* 7. BRolls */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <AlignLeft size={14} />
              <span>BRolls</span>
            </div>
            <div className="notion-prop-value">
              {editingPropKey === "brolls" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                  <input
                    type="url"
                    className="notion-prop-input"
                    placeholder="Paste B-Roll drive / file link…"
                    value={editingItem.brolls || ""}
                    autoFocus
                    onChange={(e) => setEditingItem({ ...editingItem, brolls: e.target.value })}
                    onBlur={() => setEditingPropKey(null)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingPropKey(null)}
                  />
                  <button type="button" className="notion-select-inline" onClick={() => setEditingPropKey(null)}>Done</button>
                </div>
              ) : editingItem.brolls ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {typeof editingItem.brolls === "string" && editingItem.brolls.startsWith("http") ? (
                    <a href={editingItem.brolls} target="_blank" rel="noreferrer" className="notion-link-value">
                      {editingItem.brolls}
                    </a>
                  ) : (
                    <span className="notion-plain-text">{String(editingItem.brolls || "")}</span>
                  )}
                  <button
                    type="button"
                    className="notion-close-btn"
                    style={{ padding: "2px 4px" }}
                    onClick={() => setEditingPropKey("brolls")}
                    title="Edit BRolls link"
                  >
                    <PenLine size={12} />
                  </button>
                </div>
              ) : (
                <span className="notion-empty-placeholder" onClick={() => setEditingPropKey("brolls")}>
                  Empty
                </span>
              )}
            </div>
          </div>

          {/* 8. References */}
          <div className="notion-prop-row">
            <div className="notion-prop-label">
              <AlignLeft size={14} />
              <span>References</span>
            </div>
            <div className="notion-prop-value">
              {editingPropKey === "references" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                  <input
                    type="text"
                    className="notion-prop-input"
                    placeholder="Reference link or notes…"
                    value={typeof editingItem.references === "string" ? editingItem.references : (Array.isArray(editingItem.references) ? editingItem.references.join(", ") : "")}
                    autoFocus
                    onChange={(e) => setEditingItem({ ...editingItem, references: e.target.value })}
                    onBlur={() => setEditingPropKey(null)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingPropKey(null)}
                  />
                  <button type="button" className="notion-select-inline" onClick={() => setEditingPropKey(null)}>Done</button>
                </div>
              ) : editingItem.references && (typeof editingItem.references === "string" ? editingItem.references.trim() : (Array.isArray(editingItem.references) ? editingItem.references.length > 0 : true)) ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {((typeof editingItem.references === "string" && editingItem.references.startsWith("http")) ||
                    (Array.isArray(editingItem.references) && editingItem.references.length > 0 && String(editingItem.references[0]).startsWith("http"))) ? (
                    <a
                      href={typeof editingItem.references === "string" ? editingItem.references : editingItem.references[0]}
                      target="_blank"
                      rel="noreferrer"
                      className="notion-link-value"
                    >
                      {typeof editingItem.references === "string" ? editingItem.references : editingItem.references.join(", ")}
                    </a>
                  ) : (
                    <span className="notion-plain-text">
                      {typeof editingItem.references === "string"
                        ? editingItem.references
                        : Array.isArray(editingItem.references)
                          ? editingItem.references.join(", ")
                          : String(editingItem.references || "")}
                    </span>
                  )}
                  <button
                    type="button"
                    className="notion-close-btn"
                    style={{ padding: "2px 4px" }}
                    onClick={() => setEditingPropKey("references")}
                    title="Edit References"
                  >
                    <PenLine size={12} />
                  </button>
                </div>
              ) : (
                <span className="notion-empty-placeholder" onClick={() => setEditingPropKey("references")}>
                  Empty
                </span>
              )}
            </div>
          </div>

          {/* Custom Properties */}
          {(editingItem.custom_properties || []).map((cp, idx) => (
            <div key={idx} className="notion-prop-row">
              <div className="notion-prop-label">
                <AlignLeft size={14} />
                <span>{cp.key}</span>
              </div>
              <div className="notion-prop-value">
                {typeof cp.value === "string" && cp.value.startsWith("http") ? (
                  <a href={cp.value} target="_blank" rel="noreferrer" className="notion-link-value">{cp.value}</a>
                ) : (
                  <span className="notion-plain-text">{String(cp.value ?? "Empty")}</span>
                )}
              </div>
            </div>
          ))}

          {/* + Add a property input row */}
          {showAddPropRow ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "6px" }}>
              <input
                type="text"
                className="notion-prop-input"
                style={{ maxWidth: "160px" }}
                placeholder="Property Name"
                value={newPropName}
                onChange={(e) => setNewPropName(e.target.value)}
              />
              <input
                type="text"
                className="notion-prop-input"
                placeholder="Value or URL link"
                value={newPropVal}
                onChange={(e) => setNewPropVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCustomProp()}
              />
              <button type="button" className="notion-select-inline" onClick={handleAddCustomProp}>Add</button>
              <button type="button" className="notion-close-btn" onClick={() => setShowAddPropRow(false)}>Cancel</button>
            </div>
          ) : (
            <div>
              <button
                type="button"
                className="notion-add-prop-btn"
                onClick={() => setShowAddPropRow(true)}
              >
                + Add a property
              </button>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="notion-comments-section">
          <div className="notion-comments-heading">Comments</div>
          <div className="notion-comment-input-row">
            <span className="notion-comment-avatar">
              {(user?.name || "U")[0].toUpperCase()}
            </span>
            <input
              type="text"
              className="notion-comment-input"
              placeholder="Add a comment…"
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitComment();
                }
              }}
            />
            {commentInput.trim() && (
              <button
                type="button"
                className="notion-comment-send"
                onClick={submitComment}
                title="Send comment"
              >
                <Send size={14} />
              </button>
            )}
          </div>

          {/* Comments List */}
          {editingItem.comments && editingItem.comments.length > 0 && (
            <div className="notion-comments-list">
              {editingItem.comments.map((c, i) => (
                <div key={i} className="notion-comment-bubble">
                  <div className="notion-comment-bubble-head">
                    <b>{c.user}</b>
                    {c.role && <span>({c.role})</span>}
                    {c.at && <span>· {new Date(c.at).toLocaleDateString()}</span>}
                  </div>
                  <p>{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Horizontal Divider */}
        <hr className="notion-divider" />

        {/* Script / Talking Points Content */}
        <div style={{ marginTop: "16px" }}>
          <textarea
            className="notion-content-editor"
            rows={12}
            placeholder="Write scene breakdowns, talking points, script notes or cues here…"
            value={editingItem.content || ""}
            onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })}
          />
        </div>

        {/* Modal Bottom Actions */}
        <div className="notion-modal-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {editingItem.id && (
              <button
                type="button"
                className="text-btn danger-text"
                onClick={handleDelete}
                style={{ color: "#ef4444", fontSize: "13px" }}
              >
                <Trash2 size={13} style={{ marginRight: "4px" }} /> Delete
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              className="text-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn compact"
              onClick={handleSave}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductionView({ user, token }) {
  const [items, setItems] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [periodMode, setPeriodMode] = useState("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [includeUndated, setIncludeUndated] = useState(true); // Default to true: no compulsion to assign date
  const [sortByDate, setSortByDate] = useState("asc"); // "asc" (earliest first), "desc" (latest first), "none"
  const navigate = useNavigate();

  const STAGES = ["Completed Scripts", "Recorded", "Editing", "Edited", "Uploaded"];

  const tagMap = useMemo(() => {
    const map = {};
    for (const t of tags) {
      map[t.name] = t;
    }
    return map;
  }, [tags]);

  const filteredItems = useMemo(() => {
    if (periodMode === "all") return items;

    if (periodMode === "week") {
      const { start, end } = getWeekRange(weekOffset);
      return items.filter((item) => {
        const d = parseItemDate(item);
        if (!d) return includeUndated;
        return d >= start && d <= end;
      });
    }

    if (periodMode === "month") {
      const { start, end } = getMonthRange(monthOffset);
      return items.filter((item) => {
        const d = parseItemDate(item);
        if (!d) return includeUndated;
        return d >= start && d <= end;
      });
    }

    return items;
  }, [items, periodMode, weekOffset, monthOffset, includeUndated]);

  const sortedFilteredItems = useMemo(() => {
    if (sortByDate === "none") return filteredItems;
    return [...filteredItems].sort((a, b) => {
      const dateA = a.upload_date || a.due_date || "";
      const dateB = b.upload_date || b.due_date || "";
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return sortByDate === "asc" ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
  }, [filteredItems, sortByDate]);

  const undatedCount = useMemo(() => items.filter(x => !parseItemDate(x)).length, [items]);

  const loadProduction = async () => {
    try {
      setLoading(true);
      const r = await api(token).get("/resources/content");
      setItems(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const res = await api(token).get("/team");
      setTeamMembers(res.data);
    } catch (e) {
      console.error("Unable to load team members:", e);
    }
  };

  const loadTags = async () => {
    try {
      const res = await api(token).get("/tags");
      setTags(res.data);
    } catch (e) {
      console.error("Unable to load tags:", e);
    }
  };

  useEffect(() => {
    loadProduction();
    loadTeamMembers();
    loadTags();
  }, []);

  const moveStage = async (item, dir) => {
    const curIdx = STAGES.indexOf(item.status);
    const newIdx = curIdx + dir;
    if (newIdx < 0 || newIdx >= STAGES.length) return;
    const newStage = STAGES[newIdx];
    try {
      await api(token).patch(`/resources/content/${item.id}`, { status: newStage });
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, status: newStage } : x));
    } catch (e) {
      alert("Failed to update status");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this production item?")) return;
    try {
      await api(token).delete(`/resources/content/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (editingItem?.id === id) setShowAdd(false);
    } catch (e) {
      alert("Failed to delete item");
    }
  };

  const updateCardDate = async (item, newDate) => {
    try {
      const payload = {
        upload_date: newDate || "",
        due_date: newDate || "",
      };
      await api(token).patch(`/resources/content/${item.id}`, payload);
      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id ? { ...x, upload_date: newDate || "", due_date: newDate || "" } : x
        )
      );
    } catch (e) {
      alert("Failed to update date");
    }
  };

  const openItemDetail = (item) => {
    setEditingItem(item);
    setShowAdd(true);
  };

  const openNewItem = () => {
    setEditingItem({
      title: "",
      platform: "YouTube",
      status: "Completed Scripts",
      upload_date: "",
      due_date: "",
      owner: user?.name || "Admin",
      assignee: "",
      assignee_id: "",
      arolls: "",
      audio: "",
      brolls: "",
      references: "",
      custom_properties: [],
      content: "",
      tags: [],
      comments: []
    });
    setShowAdd(true);
  };

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">STUDIO WORKFLOW</p>
          <h1>Production</h1>
          <p className="muted">Track content stages from completed scripts through editing and final uploaded release.</p>
        </div>
        <div className="prod-header-actions">
          <div className="arrange-date-bar" title="Arrange cards in production columns by date">
            <CalendarIcon size={14} className="arrange-icon" />
            <span className="arrange-text">Arrange:</span>
            <select
              value={sortByDate}
              onChange={(e) => setSortByDate(e.target.value)}
              className="arrange-select"
            >
              <option value="asc">Date: Earliest First</option>
              <option value="desc">Date: Latest First</option>
              <option value="none">Default Order</option>
            </select>
          </div>
          <button className="primary-btn compact" onClick={openNewItem}>
            <Plus size={16} /> New Production Item
          </button>
        </div>
      </div>

      {/* Time Period Filter Bar (Weekly / Monthly / All) */}
      <PeriodFilterBar
        periodMode={periodMode}
        setPeriodMode={setPeriodMode}
        weekOffset={weekOffset}
        setWeekOffset={setWeekOffset}
        monthOffset={monthOffset}
        setMonthOffset={setMonthOffset}
        totalCount={items.length}
        filteredCount={filteredItems.length}
        itemLabel="content items"
        undatedCount={undatedCount}
        includeUndated={includeUndated}
        setIncludeUndated={setIncludeUndated}
      />

      {/* 5 Column Kanban Pipeline */}
      <div className="kanban-board-5">
        {STAGES.map((stage) => {
          const colItems = sortedFilteredItems.filter((x) => {
            if (stage === "Completed Scripts") return ["Completed Scripts", "Not started"].includes(x.status);
            return x.status === stage;
          });

          return (
            <div key={stage} className={`kanban-column ${stage === "Uploaded" ? "uploaded-col" : ""}`}>
              <div className="col-header">
                <b>{stage}</b>
                <span className="col-badge">{colItems.length}</span>
              </div>
              <div className="col-body">
                {colItems.map((item) => (
                  <div key={item.id} className="kanban-card" onClick={() => openItemDetail(item)}>
                    <div className="card-top-row">
                      <span className="platform-tag">{item.platform || "Video"}</span>
                      {stage === "Uploaded" ? (
                        <span className="live-tag"><CheckCircle2 size={12} /> Live</span>
                      ) : item.assignee ? (
                        <span className="card-assignee-pill" title={`Assigned to ${item.assignee}`}>
                          <span className="avatar-micro">{(item.assignee || "U")[0].toUpperCase()}</span>
                          {item.assignee.split(" ")[0]}
                        </span>
                      ) : null}
                    </div>
                    <h4>{item.title}</h4>

                    {/* Tags visible on card */}
                    {item.tags && item.tags.length > 0 && (
                      <div className="card-tags-row">
                        {item.tags.map((tagName) => {
                          const tagObj = tagMap[tagName];
                          const color = tagObj?.color || "#6366F1";
                          return (
                            <span
                              key={tagName}
                              className="script-tag-badge"
                              style={{
                                backgroundColor: `${color}18`,
                                color: color,
                                borderColor: `${color}40`,
                              }}
                            >
                              <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                              {tagName}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {item.description && (
                      <p className="card-desc" title={item.description}>
                        {typeof item.description === "string" && item.description.length > 95
                          ? `${item.description.slice(0, 95)}…`
                          : String(item.description || "")}
                      </p>
                    )}

                    {/* Date arranger directly on card */}
                    <div className="card-date-row" onClick={(e) => e.stopPropagation()}>
                      <div className="card-date-editor">
                        <CalendarIcon size={12} className="card-date-icon" />
                        <span className="card-date-label">Date:</span>
                        <input
                          type="date"
                          className="card-inline-date-picker"
                          value={item.upload_date || item.due_date || ""}
                          onChange={(e) => updateCardDate(item, e.target.value)}
                          title="Arrange or change date directly from card"
                        />
                        {(item.upload_date || item.due_date) ? (
                          <button
                            type="button"
                            className="card-clear-date-btn"
                            onClick={() => updateCardDate(item, "")}
                            title="Clear date (make unscheduled)"
                          >
                            <X size={11} />
                          </button>
                        ) : (
                          <span className="card-no-date-hint">Set</span>
                        )}
                      </div>
                    </div>

                    {/* Inside uploaded: day to be uploaded should be link to calendar */}
                    {stage === "Uploaded" ? (
                      <div className="uploaded-calendar-link-box" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="calendar-link-btn"
                          onClick={() => navigate("/calendar")}
                        >
                          <CalendarIcon size={13} /> View in Calendar ({item.upload_date || item.due_date ? formatDateDisplay(item.upload_date || item.due_date) : "Undated"})
                        </button>
                      </div>
                    ) : (
                      <div className="stage-move-buttons" onClick={(e) => e.stopPropagation()}>
                        {STAGES.indexOf(stage) > 0 && (
                          <button className="icon-btn-micro" onClick={() => moveStage(item, -1)} title="Previous stage">
                            ←
                          </button>
                        )}
                        <button className="btn-advance" onClick={() => moveStage(item, 1)}>
                          {stage === "Completed Scripts" ? "Mark Recorded →" :
                            stage === "Recorded" ? "Send to Edit →" :
                              stage === "Editing" ? "Mark Edited →" :
                                "Mark Uploaded →"}
                        </button>
                      </div>
                    )}

                    <div className="card-foot-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="foot-icon-btn edit-btn" onClick={() => openItemDetail(item)} title="Edit details">
                        <PenLine size={13} /> Edit
                      </button>
                      <button className="foot-icon-btn delete" onClick={() => deleteItem(item.id)} title="Delete item">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}

                {colItems.length === 0 && (
                  <div className="empty-col-placeholder">
                    <span>No items {periodMode === "week" ? "this week" : periodMode === "month" ? "this month" : ""}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Production Detail Modal */}
      <ProductionDetailModal
        isOpen={showAdd}
        onClose={() => {
          setShowAdd(false);
          setEditingItem(null);
        }}
        item={editingItem}
        user={user}
        token={token}
        teamMembers={teamMembers}
        tags={tags}
        onSaveSuccess={(saved) => {
          setItems((prev) => {
            const exists = prev.some((x) => x.id === saved.id);
            return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev];
          });
        }}
        onDeleteSuccess={(delId) => {
          setItems((prev) => prev.filter((x) => x.id !== delId));
        }}
      />
    </div>
  );
}

/* ==========================================================================
   4. CONTENT CALENDAR (Linked to Uploaded & Queue)
   ========================================================================== */
export function CalendarView({ user, token }) {
  const [contents, setContents] = useState([]);
  const [tags, setTags] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date(2026, 9, 1)); // Oct 2026 default
  const [selectedDay, setSelectedDay] = useState(new Date(2026, 9, 6));
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleTitle, setScheduleTitle] = useState("");
  const [schedulePlatform, setSchedulePlatform] = useState("YouTube");
  const [scheduleDate, setScheduleDate] = useState("");

  // Search & Production Modal state
  const [searchQuery, setSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showProdModal, setShowProdModal] = useState(false);
  const [showUnscheduled, setShowUnscheduled] = useState(true);
  const [expandedScripts, setExpandedScripts] = useState({});

  const toggleExpandScript = (id) => {
    setExpandedScripts((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const loadData = async () => {
    try {
      const [rContents, rTags, rTeam] = await Promise.allSettled([
        api(token).get("/resources/content"),
        api(token).get("/tags"),
        api(token).get("/team")
      ]);
      if (rContents.status === "fulfilled") setContents(rContents.value.data);
      if (rTags.status === "fulfilled") setTags(rTags.value.data);
      if (rTeam.status === "fulfilled") setTeamMembers(rTeam.value.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadData(); }, []);

  const tagMap = useMemo(() => {
    const map = {};
    for (const t of tags) {
      map[t.name] = t;
    }
    return map;
  }, [tags]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDay(now);
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();

  const monthLabel = currentDate.toLocaleString("default", { month: "long", year: "numeric" });

  const getItemsForDate = (dayNum) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    return contents.filter((c) => {
      const d = c.upload_date || c.due_date || "";
      return d.startsWith(dateStr);
    });
  };

  const selectedDateStr = selectedDay ? `${selectedDay.getFullYear()}-${String(selectedDay.getMonth() + 1).padStart(2, "0")}-${String(selectedDay.getDate()).padStart(2, "0")}` : "";
  const selectedItems = selectedDay ? contents.filter((c) => (c.upload_date || c.due_date || "").startsWith(selectedDateStr)) : [];

  const unscheduledItems = useMemo(() => {
    return contents.filter((c) => {
      const d = c.upload_date || c.due_date || "";
      return !d || d === "TBD";
    });
  }, [contents]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return contents.filter((c) => {
      const titleMatch = (c.title || "").toLowerCase().includes(q);
      const descMatch = (c.description || "").toLowerCase().includes(q);
      const platformMatch = (c.platform || "").toLowerCase().includes(q);
      const statusMatch = (c.status || "").toLowerCase().includes(q);
      const tagsMatch = Array.isArray(c.tags) && c.tags.some(t => t.toLowerCase().includes(q));
      return titleMatch || descMatch || platformMatch || statusMatch || tagsMatch;
    });
  }, [contents, searchQuery]);

  const uploadedThisMonth = contents.filter((c) => {
    const isUploaded = c.status === "Uploaded";
    const d = c.upload_date || c.due_date || "";
    return isUploaded && d.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`);
  }).length;

  const inQueueThisMonth = contents.filter((c) => {
    const isQueued = c.status !== "Uploaded";
    const d = c.upload_date || c.due_date || "";
    return isQueued && d.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`);
  }).length;

  const openInProduction = (item) => {
    setEditingItem(item);
    setShowProdModal(true);
  };

  const quickSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleTitle.trim()) return;
    try {
      const targetDate = scheduleDate || selectedDateStr;
      const newDoc = {
        title: scheduleTitle.trim(),
        platform: schedulePlatform,
        status: "Completed Scripts",
        upload_date: targetDate,
        due_date: targetDate,
        owner: user?.name || "Admin",
      };
      const r = await api(token).post("/resources/content", newDoc);
      setContents((prev) => [r.data, ...prev]);
      setScheduleTitle("");
      setScheduleDate("");
      setShowScheduleModal(false);
    } catch (e) {
      alert("Error scheduling content");
    }
  };

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">PUBLISHING CALENDAR</p>
          <h1>Content Calendar</h1>
          <p className="muted">Linked to uploaded content — gives you visibility on what was uploaded when and what is in queue.</p>
        </div>
        <div className="calendar-stats-strip">
          <span className="cal-stat-pill green"><b>{uploadedThisMonth}</b> Uploaded this month</span>
          <span className="cal-stat-pill blue"><b>{inQueueThisMonth}</b> In queue / scheduled</span>
        </div>
      </div>

      {/* Calendar Search Bar */}
      <div className="calendar-search-section">
        <div className="calendar-search-box">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="calendar-search-input"
            placeholder="Search calendar events by title, tag, platform, status…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear-btn" onClick={() => setSearchQuery("")} title="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Real-time Search Results Panel */}
        {searchQuery.trim() && (
          <div className="calendar-search-results-panel">
            <div className="search-results-header">
              <span>Found <b>{searchResults.length}</b> {searchResults.length === 1 ? "match" : "matches"} for "{searchQuery}"</span>
              <button className="text-btn mini" onClick={() => setSearchQuery("")}>Close search results</button>
            </div>
            <div className="search-results-grid">
              {searchResults.map((item) => (
                <div
                  key={item.id}
                  className="cal-search-result-card"
                  onClick={() => {
                    const d = parseItemDate(item);
                    if (d) {
                      setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
                      setSelectedDay(d);
                    }
                    openInProduction(item);
                  }}
                >
                  <div className="res-top">
                    <span className="platform-tag">{item.platform || "Video"}</span>
                    <span className={`status-pill ${item.status === "Uploaded" ? "good" : "info"}`}>{item.status}</span>
                    <span className="res-date">
                      {item.upload_date || item.due_date ? formatDateDisplay(item.upload_date || item.due_date) : "No date"}
                    </span>
                  </div>
                  <b>{item.title}</b>
                  {item.tags && item.tags.length > 0 && (
                    <div className="res-tags-row">
                      {item.tags.map((t) => {
                        const tObj = tagMap[t];
                        const color = tObj?.color || "#6366F1";
                        return (
                          <span
                            key={t}
                            className="script-tag-badge"
                            style={{ backgroundColor: `${color}18`, color, borderColor: `${color}40` }}
                          >
                            <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                            {t}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <button className="open-prod-btn" type="button">
                    Open in Production Window <ArrowUpRight size={13} />
                  </button>
                </div>
              ))}
              {searchResults.length === 0 && (
                <div className="search-no-results">No content matches your search query.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="calendar-layout-grid">
        {/* Main Month Grid */}
        <div className="calendar-main-panel panel">
          <div className="calendar-nav-bar">
            <h2>{monthLabel}</h2>
            <div className="cal-nav-buttons">
              <button className="icon-btn-bordered" onClick={prevMonth}><ChevronLeft size={16} /></button>
              <button className="today-btn" onClick={goToday}>Today</button>
              <button className="icon-btn-bordered" onClick={nextMonth}><ChevronRight size={16} /></button>
            </div>
          </div>

          <div className="calendar-grid-header">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="calendar-grid-days">
            {Array.from({ length: firstDayIndex }).map((_, i) => (
              <div key={`blank-${i}`} className="cal-day-cell blank" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const dayItems = getItemsForDate(dayNum);
              const isSelected = selectedDay && selectedDay.getDate() === dayNum && selectedDay.getMonth() === month && selectedDay.getFullYear() === year;

              return (
                <div
                  key={`day-${dayNum}`}
                  className={`cal-day-cell ${isSelected ? "selected" : ""} ${dayItems.length > 0 ? "has-events" : ""}`}
                  onClick={() => setSelectedDay(new Date(year, month, dayNum))}
                >
                  <div className="day-number-row">
                    <span className="day-number">{dayNum}</span>
                    {dayItems.length > 0 && <span className="day-count-dot">{dayItems.length}</span>}
                  </div>
                  <div className="day-events-list">
                    {dayItems.slice(0, 3).map((ev) => (
                      <div
                        key={ev.id}
                        className={`cal-event-pill ${ev.status === "Uploaded" ? "uploaded" : "queued"}`}
                        title={`${ev.title}${ev.tags?.length ? ` · Tags: ${ev.tags.join(", ")}` : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDay(new Date(year, month, dayNum));
                          openInProduction(ev);
                        }}
                      >
                        <span className="ev-icon">{ev.status === "Uploaded" ? "✓" : "•"}</span>
                        <span className="ev-title">{ev.title}</span>
                        {/* Tags visible on calendar event pill */}
                        {ev.tags && ev.tags.length > 0 && (
                          <div className="cal-event-tags-row">
                            {ev.tags.slice(0, 2).map((tn) => {
                              const tObj = tagMap[tn];
                              const color = tObj?.color || "#6366F1";
                              return (
                                <span
                                  key={tn}
                                  className="cal-tag-pill-mini"
                                  style={{
                                    backgroundColor: `${color}25`,
                                    color: color,
                                    borderColor: `${color}50`
                                  }}
                                  title={tn}
                                >
                                  {tn}
                                </span>
                              );
                            })}
                            {ev.tags.length > 2 && (
                              <span className="cal-tag-more">+{ev.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {dayItems.length > 3 && (
                      <span className="more-tag">+{dayItems.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Date Details Sidebar */}
        <div className="calendar-side-panel panel">
          <div className="side-panel-header">
            <div>
              <p className="eyebrow">DAY SCHEDULE</p>
              <h3>{selectedDay ? selectedDay.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Select a date"}</h3>
            </div>
            <button className="primary-btn compact" onClick={() => { setScheduleDate(selectedDateStr); setShowScheduleModal(true); }}>
              <Plus size={14} /> Schedule
            </button>
          </div>

          <div className="day-schedule-items">
            {selectedItems.map((item) => (
              <div
                key={item.id}
                className="schedule-item-card clickable"
                onClick={() => openInProduction(item)}
                title="Click to open Production Window"
              >
                <div className="sch-top">
                  <span className="platform-tag">{item.platform || "Video"}</span>
                  <span className={`status-pill ${item.status === "Uploaded" ? "good" : "info"}`}>{item.status}</span>
                </div>
                <b>{item.title}</b>

                {/* Tags visible on calendar day schedule card */}
                {item.tags && item.tags.length > 0 && (
                  <div className="sch-tags-row">
                    {item.tags.map((t) => {
                      const tObj = tagMap[t];
                      const color = tObj?.color || "#6366F1";
                      return (
                        <span
                          key={t}
                          className="script-tag-badge"
                          style={{ backgroundColor: `${color}18`, color, borderColor: `${color}40` }}
                        >
                          <span className="tag-badge-dot" style={{ backgroundColor: color }} />
                          {t}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Script preview / expandable */}
                {(() => {
                  const scriptText = item.description || item.content || item.notes || "";
                  if (!scriptText) return null;
                  const isExpanded = Boolean(expandedScripts[item.id]);
                  const isLong = scriptText.length > 130;
                  return (
                    <div className="sch-script-container" onClick={(e) => e.stopPropagation()}>
                      <p className={`sch-desc ${isExpanded ? "expanded" : "collapsed"}`}>
                        {!isExpanded && isLong ? `${scriptText.slice(0, 130).trim()}…` : scriptText}
                      </p>
                      {isLong && (
                        <button
                          type="button"
                          className="sch-script-expand-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandScript(item.id);
                          }}
                        >
                          {isExpanded ? (
                            <>
                              Collapse script <ChevronUp size={12} />
                            </>
                          ) : (
                            <>
                              Expand details <ChevronDown size={12} />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })()}

                <div className="sch-card-footer">
                  <button className="sch-open-prod-btn" type="button">
                    Open in Production <ArrowUpRight size={12} />
                  </button>
                </div>
              </div>
            ))}

            {selectedItems.length === 0 && (
              <div className="empty-day-state">
                <CalendarIcon size={24} />
                <p>No content scheduled or uploaded for this day.</p>
                <button className="text-btn" onClick={() => { setScheduleDate(selectedDateStr); setShowScheduleModal(true); }}>
                  + Schedule a video for this date
                </button>
              </div>
            )}
          </div>

          {/* Unscheduled Content Drawer (No compulsion to assign date) */}
          {unscheduledItems.length > 0 && (
            <div className="unscheduled-drawer-section">
              <div className="unscheduled-head" onClick={() => setShowUnscheduled(!showUnscheduled)}>
                <b>Unscheduled Content ({unscheduledItems.length})</b>
                <span className="toggle-sym">{showUnscheduled ? "▲" : "▼"}</span>
              </div>
              {showUnscheduled && (
                <div className="unscheduled-list">
                  {unscheduledItems.map((item) => (
                    <div
                      key={item.id}
                      className="unscheduled-item-row"
                      onClick={() => openInProduction(item)}
                      title="Click to open Production Window and assign date or edit"
                    >
                      <span className="unscheduled-title">{item.title}</span>
                      <span className={`status-pill mini ${item.status === "Uploaded" ? "good" : "info"}`}>{item.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-backdrop" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Schedule Content</h2>
              <button className="icon-btn" onClick={() => setShowScheduleModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={quickSchedule}>
              <label>Target Date (Optional)</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                placeholder="No compulsion to assign date"
              />

              <label>Video / Post Title</label>
              <input
                required
                type="text"
                placeholder="e.g. My 2026 Creator Tech Stack Breakdown"
                value={scheduleTitle}
                onChange={(e) => setScheduleTitle(e.target.value)}
              />

              <label>Platform</label>
              <select value={schedulePlatform} onChange={(e) => setSchedulePlatform(e.target.value)}>
                <option value="YouTube">YouTube Main Video</option>
                <option value="Instagram Reels">Instagram Reels</option>
                <option value="Shorts">YouTube Shorts</option>
                <option value="TikTok">TikTok</option>
              </select>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setShowScheduleModal(false)}>Cancel</button>
                <button type="submit" className="primary-btn compact">Confirm Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Production Window Modal */}
      <ProductionDetailModal
        isOpen={showProdModal}
        onClose={() => {
          setShowProdModal(false);
          setEditingItem(null);
        }}
        item={editingItem}
        user={user}
        token={token}
        teamMembers={teamMembers}
        tags={tags}
        onSaveSuccess={(savedItem) => {
          setContents((prev) => {
            const exists = prev.some((x) => x.id === savedItem.id);
            return exists ? prev.map((x) => (x.id === savedItem.id ? savedItem : x)) : [savedItem, ...prev];
          });
        }}
        onDeleteSuccess={(delId) => {
          setContents((prev) => prev.filter((x) => x.id !== delId));
        }}
      />
    </div>
  );
}

/* ==========================================================================
   5. BRAND DEALS (Leads -> Locked -> Uploaded -> Payment completed)
   ========================================================================== */
export function BrandDealsView({ user, token }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newDeal, setNewDeal] = useState({
    title: "", brand: "", stage: "Leads", value: "", due_date: "", description: ""
  });
  const [editingDeal, setEditingDeal] = useState(null);
  const [periodMode, setPeriodMode] = useState("week"); // Default to weekly ("hamesa week ka dikhaye")
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [includeUndated, setIncludeUndated] = useState(false);

  const DEAL_STAGES = ["Leads", "Locked", "Uploaded", "Payment completed"];

  const loadDeals = async () => {
    try {
      setLoading(true);
      const r = await api(token).get("/resources/deal");
      setDeals(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeals(); }, []);

  const filteredDeals = useMemo(() => {
    if (periodMode === "all") return deals;

    if (periodMode === "week") {
      const { start, end } = getWeekRange(weekOffset);
      return deals.filter((d) => {
        const dt = parseItemDate(d);
        if (!dt) return includeUndated;
        return dt >= start && dt <= end;
      });
    }

    if (periodMode === "month") {
      const { start, end } = getMonthRange(monthOffset);
      return deals.filter((d) => {
        const dt = parseItemDate(d);
        if (!dt) return includeUndated;
        return dt >= start && dt <= end;
      });
    }

    return deals;
  }, [deals, periodMode, weekOffset, monthOffset, includeUndated]);

  const undatedDealsCount = useMemo(() => deals.filter(x => !parseItemDate(x)).length, [deals]);

  const moveStage = async (deal, dir) => {
    const curIdx = DEAL_STAGES.indexOf(deal.stage || "Leads");
    const nextIdx = curIdx + dir;
    if (nextIdx < 0 || nextIdx >= DEAL_STAGES.length) return;
    const nextStage = DEAL_STAGES[nextIdx];
    try {
      await api(token).patch(`/resources/deal/${deal.id}`, { stage: nextStage, status: nextStage });
      setDeals((prev) => prev.map((d) => d.id === deal.id ? { ...d, stage: nextStage, status: nextStage } : d));
    } catch (e) {
      alert("Error updating deal stage");
    }
  };

  const deleteDeal = async (id) => {
    if (!window.confirm("Delete this brand deal?")) return;
    try {
      await api(token).delete(`/resources/deal/${id}`);
      setDeals((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      alert("Failed to delete deal");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const doc = {
        ...newDeal,
        due_date: newDeal.due_date || new Date().toISOString().split("T")[0],
        value: Number(newDeal.value) || 0,
        status: newDeal.stage,
        owner: user.name,
      };
      const r = await api(token).post("/resources/deal", doc);
      setDeals((prev) => [r.data, ...prev]);
      setShowAdd(false);
      setNewDeal({ title: "", brand: "", stage: "Leads", value: "", due_date: "", description: "" });
    } catch (e) {
      alert("Error adding deal");
    }
  };

  const openEditDeal = (deal) => {
    setEditingDeal({
      id: deal.id,
      brand: deal.brand || "",
      title: deal.title || "",
      value: deal.value !== undefined && deal.value !== null ? String(deal.value) : "",
      stage: deal.stage || deal.status || "Leads",
      due_date: deal.due_date || "",
      description: deal.description || ""
    });
  };

  const handleUpdateDeal = async (e) => {
    e.preventDefault();
    if (!editingDeal) return;
    try {
      const doc = {
        brand: editingDeal.brand,
        title: editingDeal.title,
        value: Number(editingDeal.value) || 0,
        stage: editingDeal.stage,
        status: editingDeal.stage,
        due_date: editingDeal.due_date || "",
        description: editingDeal.description || ""
      };
      const r = await api(token).patch(`/resources/deal/${editingDeal.id}`, doc);
      setDeals((prev) => prev.map((d) => (d.id === editingDeal.id ? { ...d, ...r.data, ...doc } : d)));
      setEditingDeal(null);
    } catch (e) {
      alert("Error updating brand deal");
    }
  };

  const totalPipeline = filteredDeals.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
  const paidTotal = filteredDeals.filter(d => (d.stage || d.status) === "Payment completed").reduce((acc, d) => acc + (Number(d.value) || 0), 0);
  const activeCount = filteredDeals.filter(d => (d.stage || d.status) !== "Payment completed").length;

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">MONETIZATION PIPELINE</p>
          <h1>Brand Deals</h1>
          <p className="muted">Sponsorships from initial leads, contract lock, content upload, to payment completion.</p>
        </div>
        <button className="primary-btn compact" onClick={() => setShowAdd(true)}>
          <Plus size={16} /> New Brand Deal
        </button>
      </div>

      {/* Time Period Filter Bar (Weekly / Monthly / All) */}
      <PeriodFilterBar
        periodMode={periodMode}
        setPeriodMode={setPeriodMode}
        weekOffset={weekOffset}
        setWeekOffset={setWeekOffset}
        monthOffset={monthOffset}
        setMonthOffset={setMonthOffset}
        totalCount={deals.length}
        filteredCount={filteredDeals.length}
        itemLabel="brand deals"
        undatedCount={undatedDealsCount}
        includeUndated={includeUndated}
        setIncludeUndated={setIncludeUndated}
      />

      {/* Summary strip */}
      <div className="deals-top-strip">
        <div className="deal-stat">
          <small>{periodMode === "week" ? "Pipeline Value (Week)" : periodMode === "month" ? "Pipeline Value (Month)" : "Total Pipeline Value"}</small>
          <strong>{money(totalPipeline)}</strong>
        </div>
        <div className="deal-stat">
          <small>{periodMode === "week" ? "Paid Completed (Week)" : periodMode === "month" ? "Paid Completed (Month)" : "Payment Completed"}</small>
          <strong className="green-text">{money(paidTotal)}</strong>
        </div>
        <div className="deal-stat">
          <small>Active Deals in Flight</small>
          <strong>{activeCount}</strong>
        </div>
      </div>

      {/* 4 Pipeline Columns */}
      <div className="kanban-board-4">
        {DEAL_STAGES.map((stage) => {
          const colDeals = filteredDeals.filter((d) => (d.stage || d.status) === stage);

          return (
            <div key={stage} className={`kanban-column ${stage === "Payment completed" ? "paid-col" : ""}`}>
              <div className="col-header">
                <b>{stage}</b>
                <span className="col-badge">{colDeals.length}</span>
              </div>
              <div className="col-body">
                {colDeals.map((d) => (
                  <div key={d.id} className="kanban-card deal-card">
                    <div className="card-top-row">
                      <b className="deal-brand-name">{d.brand || "Brand"}</b>
                      <span className="deal-price">{money(d.value)}</span>
                    </div>
                    <h4 style={{ cursor: "pointer" }} onClick={() => openEditDeal(d)} title="Click to edit deal">{d.title}</h4>
                    {d.description && <p className="card-desc">{d.description}</p>}
                    <small className="due-tag">Due: {d.due_date || "TBD"}</small>

                    <div className="stage-move-buttons">
                      {DEAL_STAGES.indexOf(stage) > 0 && (
                        <button className="icon-btn-micro" onClick={() => moveStage(d, -1)}>←</button>
                      )}
                      {stage !== "Payment completed" && (
                        <button className="btn-advance" onClick={() => moveStage(d, 1)}>
                          {stage === "Leads" ? "Lock Deal →" :
                            stage === "Locked" ? "Mark Uploaded →" :
                              "Mark Paid →"}
                        </button>
                      )}
                      {stage === "Payment completed" && (
                        <span className="paid-confirmed-badge">✓ Paid & Closed</span>
                      )}
                    </div>

                    <div className="card-foot-actions">
                      <button className="foot-icon-btn edit" onClick={() => openEditDeal(d)} title="Edit Deal">
                        <Edit2 size={13} /> Edit
                      </button>
                      <button className="foot-icon-btn delete" onClick={() => deleteDeal(d.id)} title="Delete Deal">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}

                {colDeals.length === 0 && (
                  <div className="empty-col-placeholder">
                    <span>No deals {periodMode === "week" ? "this week" : periodMode === "month" ? "this month" : ""}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Deal Modal */}
      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>New Brand Deal</h2>
              <button className="icon-btn" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <label>Brand Name</label>
              <input
                required
                type="text"
                placeholder="e.g. Notion, Logitech, NordVPN"
                value={newDeal.brand}
                onChange={(e) => setNewDeal({ ...newDeal, brand: e.target.value })}
              />

              <label>Campaign / Deliverable Title</label>
              <input
                required
                type="text"
                placeholder="e.g. 60s Dedicated Integration + 1 IG Reel"
                value={newDeal.title}
                onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label>Deal Value (₹)</label>
                  <input
                    required
                    type="number"
                    placeholder="35000"
                    value={newDeal.value}
                    onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                  />
                </div>
                <div>
                  <label>Stage</label>
                  <select
                    value={newDeal.stage}
                    onChange={(e) => setNewDeal({ ...newDeal, stage: e.target.value })}
                  >
                    {DEAL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <label>Target Delivery Date</label>
              <input
                type="date"
                value={newDeal.due_date}
                onChange={(e) => setNewDeal({ ...newDeal, due_date: e.target.value })}
              />

              <label>Campaign Scope / Notes</label>
              <textarea
                rows={3}
                placeholder="Brief requirements, tracking link, contract details…"
                value={newDeal.description}
                onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="primary-btn compact">Create Deal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Deal Modal */}
      {editingDeal && (
        <div className="modal-backdrop" onClick={() => setEditingDeal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Edit Brand Deal</h2>
              <button className="icon-btn" onClick={() => setEditingDeal(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleUpdateDeal}>
              <label>Brand Name</label>
              <input
                required
                type="text"
                placeholder="e.g. Notion, Logitech, NordVPN"
                value={editingDeal.brand}
                onChange={(e) => setEditingDeal({ ...editingDeal, brand: e.target.value })}
              />

              <label>Campaign / Deliverable Title</label>
              <input
                required
                type="text"
                placeholder="e.g. 60s Dedicated Integration + 1 IG Reel"
                value={editingDeal.title}
                onChange={(e) => setEditingDeal({ ...editingDeal, title: e.target.value })}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label>Deal Value (₹)</label>
                  <input
                    required
                    type="number"
                    placeholder="35000"
                    value={editingDeal.value}
                    onChange={(e) => setEditingDeal({ ...editingDeal, value: e.target.value })}
                  />
                </div>
                <div>
                  <label>Stage</label>
                  <select
                    value={editingDeal.stage}
                    onChange={(e) => setEditingDeal({ ...editingDeal, stage: e.target.value })}
                  >
                    {DEAL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <label>Target Delivery Date</label>
              <input
                type="date"
                value={editingDeal.due_date}
                onChange={(e) => setEditingDeal({ ...editingDeal, due_date: e.target.value })}
              />

              <label>Campaign Scope / Notes</label>
              <textarea
                rows={3}
                placeholder="Brief requirements, tracking link, contract details…"
                value={editingDeal.description}
                onChange={(e) => setEditingDeal({ ...editingDeal, description: e.target.value })}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setEditingDeal(null)}>Cancel</button>
                <button type="submit" className="primary-btn compact">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   6. FINANCES (Debit/Credit Ledger + Profit/Loss + Filters + Chart)
   ========================================================================== */
export function FinancesView({ user, token }) {
  const [txs, setTxs] = useState([]);
  const [overview, setOverview] = useState({ total_credit: 0, total_debit: 0, profit_loss: 0, monthly_chart: [] });
  const [dateFilter, setDateFilter] = useState("all"); // all | this_month | last_month | custom
  const [typeFilter, setTypeFilter] = useState("all"); // all | Credit | Debit
  const [search, setSearch] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    service: "Instagram",
    name: "",
    date: new Date().toISOString().split("T")[0],
    type: "Credit",
    amount: "",
    notes: "",
    invoice_url: "",
    invoice_name: ""
  });

  // Statement Export State
  const [showExport, setShowExport] = useState(false);
  const [exportPreset, setExportPreset] = useState("all");
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [exportType, setExportType] = useState("all");

  // Invoice Attachment State for existing transaction
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachForm, setAttachForm] = useState({ invoice_url: "", invoice_name: "" });
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Edit Transaction State
  const [editingTx, setEditingTx] = useState(null);
  const [editForm, setEditForm] = useState({
    service: "Instagram",
    name: "",
    date: new Date().toISOString().split("T")[0],
    type: "Credit",
    amount: "",
    notes: "",
    invoice_url: "",
    invoice_name: ""
  });

  const loadFinances = async () => {
    try {
      setLoading(true);
      const [rTx, rOver] = await Promise.all([
        api(token).get("/finances/transactions"),
        api(token).get("/finances/overview")
      ]);
      setTxs(rTx.data);
      setOverview(rOver.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFinances(); }, []);

  const handleUploadInvoiceFile = async (file, target = "new") => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploadingInvoice(true);
    try {
      const res = await api(token).post("/finances/upload-invoice", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (target === "new" || target === true) {
        setForm(f => ({ ...f, invoice_url: res.data.url, invoice_name: res.data.name }));
      } else if (target === "edit") {
        setEditForm(f => ({ ...f, invoice_url: res.data.url, invoice_name: res.data.name }));
      } else {
        setAttachForm({ invoice_url: res.data.url, invoice_name: res.data.name });
      }
    } catch (err) {
      console.error(err);
      alert("Failed to upload invoice file. Please check file format and size.");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const openEditTx = (t) => {
    setEditingTx(t);
    setEditForm({
      service: t.service || "",
      name: t.name || "",
      date: t.date || new Date().toISOString().split("T")[0],
      type: t.type || "Credit",
      amount: t.amount !== undefined && t.amount !== null ? String(t.amount) : "",
      notes: t.notes || "",
      invoice_url: t.invoice_url || "",
      invoice_name: t.invoice_name || ""
    });
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!editingTx || !editForm.service || !editForm.name || !editForm.amount) return;
    try {
      await api(token).patch(`/finances/transactions/${editingTx.id}`, {
        service: editForm.service.trim(),
        name: editForm.name.trim(),
        date: editForm.date,
        type: editForm.type,
        amount: Number(editForm.amount),
        notes: (editForm.notes || "").trim(),
        invoice_url: (editForm.invoice_url || "").trim(),
        invoice_name: (editForm.invoice_name || "").trim()
      });
      setEditingTx(null);
      loadFinances();
    } catch (err) {
      alert("Error updating transaction entry");
    }
  };

  const handleSaveInvoiceToTransaction = async (e) => {
    if (e) e.preventDefault();
    if (!attachTarget) return;
    try {
      await api(token).patch(`/finances/transactions/${attachTarget.id}`, {
        invoice_url: attachForm.invoice_url,
        invoice_name: attachForm.invoice_name || (attachForm.invoice_url ? "Invoice" : "")
      });
      setAttachTarget(null);
      loadFinances();
    } catch (err) {
      alert("Failed to attach invoice to transaction");
    }
  };

  const handleRemoveInvoiceFromTransaction = async (transId) => {
    if (!window.confirm("Remove invoice attachment from this transaction?")) return;
    try {
      await api(token).patch(`/finances/transactions/${transId}`, {
        invoice_url: "",
        invoice_name: ""
      });
      if (attachTarget?.id === transId) {
        setAttachTarget(null);
      }
      loadFinances();
    } catch (err) {
      alert("Failed to remove invoice");
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (!form.service || !form.name || !form.amount) return;
    try {
      await api(token).post("/finances/transactions", {
        ...form,
        amount: Number(form.amount)
      });
      setShowAdd(false);
      setForm({
        service: "Instagram",
        name: "",
        date: new Date().toISOString().split("T")[0],
        type: "Credit",
        amount: "",
        notes: "",
        invoice_url: "",
        invoice_name: ""
      });
      loadFinances();
    } catch (err) {
      alert("Error adding transaction");
    }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction entry?")) return;
    try {
      await api(token).delete(`/finances/transactions/${id}`);
      loadFinances();
    } catch (e) {
      alert("Error deleting transaction");
    }
  };

  // Filter logic
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, "0")}`;

  const filtered = useMemo(() => {
    return txs.filter((t) => {
      // Type filter
      if (typeFilter !== "all" && t.type !== typeFilter) return false;

      // Date filter
      const d = t.date || "";
      if (dateFilter === "this_month") {
        if (!d.startsWith(currentMonthKey) && !d.includes("Oct 2026")) return false;
      } else if (dateFilter === "last_month") {
        if (!d.startsWith(lastMonthKey) && !d.includes("Sep 2026")) return false;
      } else if (dateFilter === "custom") {
        if (customStart && d < customStart) return false;
        if (customEnd && d > customEnd) return false;
      }

      // Search filter
      if (search) {
        const q = search.toLowerCase();
        const match = (t.service || "").toLowerCase().includes(q) || (t.name || "").toLowerCase().includes(q) || (t.notes || "").toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [txs, typeFilter, dateFilter, customStart, customEnd, search, currentMonthKey, lastMonthKey]);

  // Compute filtered totals
  const totalCredit = filtered.filter(t => t.type === "Credit").reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  const totalDebit = filtered.filter(t => t.type === "Debit").reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  const netProfitLoss = totalCredit - totalDebit;

  // Running cumulative profit/loss calculation (ascending by date, then map to table order)
  const txsWithRunning = useMemo(() => {
    // Sort oldest first to compute cumulative
    const sortedOldestFirst = [...filtered].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    let running = 0;
    const runningMap = {};
    sortedOldestFirst.forEach((t) => {
      const amt = Number(t.amount) || 0;
      if (t.type === "Credit") running += amt;
      else running -= amt;
      runningMap[t.id] = running;
    });

    // Return current sorted order (newest first) with running attached
    return filtered.map((t) => ({ ...t, running_balance: runningMap[t.id] ?? 0 }));
  }, [filtered]);

  // Chart max calculation for monthly chart
  const monthlyChart = overview.monthly_chart || [];
  const maxMonthlyVal = Math.max(1, ...monthlyChart.map(m => Math.max(m.credit, m.debit)));

  // Statement Export Handlers & Calculations
  const openExportModal = () => {
    if (dateFilter === "this_month") {
      setExportPreset("this_month");
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
      setExportStart(`${y}-${m}-01`);
      setExportEnd(`${y}-${m}-${lastDay}`);
    } else if (dateFilter === "last_month") {
      setExportPreset("last_month");
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const y = lm.getFullYear();
      const m = String(lm.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(y, lm.getMonth() + 1, 0).getDate();
      setExportStart(`${y}-${m}-01`);
      setExportEnd(`${y}-${m}-${lastDay}`);
    } else if (dateFilter === "custom" && (customStart || customEnd)) {
      setExportPreset("custom");
      setExportStart(customStart);
      setExportEnd(customEnd);
    } else {
      setExportPreset("all");
      setExportStart("");
      setExportEnd("");
    }
    setExportType(typeFilter);
    setShowExport(true);
  };

  const handleExportPresetChange = (preset) => {
    setExportPreset(preset);
    const curr = new Date();
    const y = curr.getFullYear();
    const m = curr.getMonth();

    if (preset === "all") {
      setExportStart("");
      setExportEnd("");
    } else if (preset === "this_month") {
      const monthStr = String(m + 1).padStart(2, "0");
      const lastDay = new Date(y, m + 1, 0).getDate();
      setExportStart(`${y}-${monthStr}-01`);
      setExportEnd(`${y}-${monthStr}-${lastDay}`);
    } else if (preset === "last_month") {
      const lm = new Date(y, m - 1, 1);
      const ly = lm.getFullYear();
      const lmStr = String(lm.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(ly, lm.getMonth() + 1, 0).getDate();
      setExportStart(`${ly}-${lmStr}-01`);
      setExportEnd(`${ly}-${lmStr}-${lastDay}`);
    } else if (preset === "last_3_months") {
      const past = new Date();
      past.setDate(past.getDate() - 90);
      setExportStart(past.toISOString().split("T")[0]);
      setExportEnd(curr.toISOString().split("T")[0]);
    } else if (preset === "ytd") {
      setExportStart(`${y}-01-01`);
      setExportEnd(curr.toISOString().split("T")[0]);
    }
  };

  const exportFilteredRecords = useMemo(() => {
    return txs.filter((t) => {
      if (exportType !== "all" && t.type !== exportType) return false;
      const d = t.date || "";
      if (exportStart && d < exportStart) return false;
      if (exportEnd && d > exportEnd) return false;
      return true;
    });
  }, [txs, exportType, exportStart, exportEnd]);

  const exportTotalCredit = exportFilteredRecords.filter(t => t.type === "Credit").reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const exportTotalDebit = exportFilteredRecords.filter(t => t.type === "Debit").reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const exportNetBalance = exportTotalCredit - exportTotalDebit;

  const downloadCSV = (records, rangeLabel) => {
    if (!records || records.length === 0) {
      alert("No transactions found for the selected date range.");
      return;
    }
    const sorted = [...records].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const totalCred = sorted.filter(t => t.type === "Credit").reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const totalDeb = sorted.filter(t => t.type === "Debit").reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const netTotal = totalCred - totalDeb;

    const escapeCsv = (str) => {
      if (str === null || str === undefined) return '""';
      const s = String(str).replace(/"/g, '""');
      return `"${s}"`;
    };

    const rows = [
      ["CONTENT HQ - TRANSACTION STATEMENT"],
      [`Date Range: ${rangeLabel.replace(/_/g, " ")}`],
      [`Generated On: ${new Date().toLocaleString()}`],
      [`Total Income (Credit): Rs. ${totalCred.toLocaleString("en-IN")}`],
      [`Total Expenses (Debit): Rs. ${totalDeb.toLocaleString("en-IN")}`],
      [`Net Margin (Profit/Loss): Rs. ${netTotal.toLocaleString("en-IN")}`],
      [],
      [
        "Date",
        "Service / Category",
        "Name / Description",
        "Type",
        "Debit (INR)",
        "Credit (INR)",
        "Running Balance (INR)",
        "Notes",
        "Invoice Attached",
        "Invoice URL"
      ]
    ];

    let running = 0;
    sorted.forEach((t) => {
      const amt = Number(t.amount) || 0;
      const isCredit = t.type === "Credit";
      if (isCredit) running += amt;
      else running -= amt;

      const fullInvoiceUrl = t.invoice_url ? (t.invoice_url.startsWith("http") ? t.invoice_url : `${BASE_URL}${t.invoice_url}`) : "";

      rows.push([
        t.date || "",
        t.service || "",
        t.name || "",
        t.type || "",
        !isCredit ? amt.toFixed(2) : "",
        isCredit ? amt.toFixed(2) : "",
        running.toFixed(2),
        t.notes || "",
        t.invoice_url ? (t.invoice_name || "Yes") : "No",
        fullInvoiceUrl
      ]);
    });

    rows.push([]);
    rows.push([
      "TOTALS",
      "",
      `Total Transactions: ${sorted.length}`,
      "",
      totalDeb.toFixed(2),
      totalCred.toFixed(2),
      netTotal.toFixed(2),
      "",
      "",
      ""
    ]);

    const csvContent = rows.map(r => r.map(escapeCsv).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const sanitizedRange = rangeLabel.replace(/[^a-zA-Z0-9_-]/g, "_");
    link.setAttribute("href", url);
    link.setAttribute("download", `ContentHQ_Transactions_${sanitizedRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const triggerExportDownload = () => {
    const rangeLabel = exportStart && exportEnd
      ? `${exportStart}_to_${exportEnd}`
      : exportStart
        ? `from_${exportStart}`
        : exportEnd
          ? `until_${exportEnd}`
          : "All_Time";
    downloadCSV(exportFilteredRecords, rangeLabel);
    setShowExport(false);
  };

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">STUDIO LEDGER & ACCOUNTS</p>
          <h1>Finances</h1>
          <p className="muted">Track income, subscriptions, gear expenses, and auto-calculated real-time profit/loss.</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button className="secondary-btn compact" onClick={openExportModal} title="Download Statement CSV with custom date range">
            <Download size={15} /> Download Statement
          </button>
          <button className="primary-btn compact" onClick={() => setShowAdd(true)}>
            <Plus size={16} /> Add Transaction
          </button>
        </div>
      </div>

      {/* Top Summary Cards (User specified) */}
      <div className="finances-summary-grid">
        <div className="fin-summary-card">
          <small>Total Income / Credit</small>
          <strong className="credit-text">{money(totalCredit)}</strong>
          <span>Verified inflows</span>
        </div>
        <div className="fin-summary-card">
          <small>Total Expenses / Debit</small>
          <strong className="debit-text">{money(totalDebit)}</strong>
          <span>Studio costs & tools</span>
        </div>
        <div className={`fin-summary-card ${netProfitLoss >= 0 ? "profit-card" : "loss-card"}`}>
          <small>Overall Profit / Loss</small>
          <strong className={netProfitLoss >= 0 ? "profit-text" : "loss-text"}>{money(netProfitLoss)}</strong>
          <span>Profit/Loss = Total Credit − Total Debit</span>
        </div>
      </div>

      {/* Monthly Profit/Loss Visual Chart */}
      <section className="panel monthly-chart-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">MONTHLY PERFORMANCE</p>
            <h2>Monthly Profit / Loss Trend</h2>
          </div>
          <div className="chart-legend">
            <span className="legend-item"><i className="bar-credit" /> Credit (Income)</span>
            <span className="legend-item"><i className="bar-debit" /> Debit (Expenses)</span>
            <span className="legend-item"><i className="bar-margin" /> Net Margin</span>
          </div>
        </div>

        <div className="chart-bars-wrap">
          {monthlyChart.map((m) => {
            const creditPct = Math.round((m.credit / maxMonthlyVal) * 100);
            const debitPct = Math.round((m.debit / maxMonthlyVal) * 100);

            return (
              <div key={m.key} className="chart-month-col">
                <div className="bars-container">
                  <div className="bar-wrapper">
                    <div className="bar credit" style={{ height: `${creditPct}%` }} title={`Credit: ${money(m.credit)}`} />
                    <div className="bar debit" style={{ height: `${debitPct}%` }} title={`Debit: ${money(m.debit)}`} />
                  </div>
                </div>
                <b className="month-label">{m.label}</b>
                <span className={`month-net ${m.profit_loss >= 0 ? "good" : "bad"}`}>{money(m.profit_loss)}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Filters Strip (User specified) */}
      <div className="finances-filters-bar">
        <div className="filter-group">
          <button className={dateFilter === "all" ? "filter-pill active" : "filter-pill"} onClick={() => setDateFilter("all")}>
            All Time
          </button>
          <button className={dateFilter === "this_month" ? "filter-pill active" : "filter-pill"} onClick={() => setDateFilter("this_month")}>
            This Month
          </button>
          <button className={dateFilter === "last_month" ? "filter-pill active" : "filter-pill"} onClick={() => setDateFilter("last_month")}>
            Last Month
          </button>
          <button className={dateFilter === "custom" ? "filter-pill active" : "filter-pill"} onClick={() => setDateFilter("custom")}>
            Custom Date Range
          </button>
        </div>

        <div className="filter-group">
          <button className={typeFilter === "all" ? "filter-pill active" : "filter-pill"} onClick={() => setTypeFilter("all")}>
            All Types
          </button>
          <button className={typeFilter === "Credit" ? "filter-pill active" : "filter-pill"} onClick={() => setTypeFilter("Credit")}>
            Income (Credit)
          </button>
          <button className={typeFilter === "Debit" ? "filter-pill active" : "filter-pill"} onClick={() => setTypeFilter("Debit")}>
            Expenses (Debit)
          </button>
        </div>

        <div className="search-wrap compact">
          <Search size={14} />
          <input
            type="text"
            placeholder="Filter by service or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {dateFilter === "custom" && (
        <div className="custom-range-inputs">
          <label>From: <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} /></label>
          <label>To: <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} /></label>
        </div>
      )}

      {/* Transaction Table (User specified structure) */}
      <div className="panel table-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">TRANSACTION LEDGER</p>
            <h2>Recorded Transactions ({txsWithRunning.length})</h2>
          </div>
        </div>

        <div className="ledger-table-wrap">
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Name / Description</th>
                <th>Date</th>
                <th>Invoice</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-right">overall (Profit/Loss)</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {txsWithRunning.map((t) => {
                const isCredit = t.type === "Credit";
                return (
                  <tr key={t.id}>
                    <td>
                      <span className="service-badge">{t.service}</span>
                    </td>
                    <td>
                      <div className="desc-cell" style={{ cursor: "pointer" }} onClick={() => openEditTx(t)} title="Click to edit transaction">
                        <b>{t.name}</b>
                        {t.notes && <small>{t.notes}</small>}
                      </div>
                    </td>
                    <td>{t.date}</td>
                    <td className="invoice-col">
                      {t.invoice_url ? (
                        <div className="invoice-chip-group">
                          <a
                            href={t.invoice_url.startsWith("http") ? t.invoice_url : `${BASE_URL}${t.invoice_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="invoice-badge"
                            title={t.invoice_name || "View Invoice"}
                          >
                            <FileText size={12} />
                            <span className="invoice-name-text">{t.invoice_name || "Invoice"}</span>
                            <ExternalLink size={10} className="ext-icon" />
                          </a>
                          <button
                            type="button"
                            className="icon-btn-micro"
                            onClick={() => {
                              setAttachTarget(t);
                              setAttachForm({ invoice_url: t.invoice_url || "", invoice_name: t.invoice_name || "" });
                            }}
                            title="Edit or replace invoice"
                          >
                            <Edit2 size={11} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="attach-invoice-btn"
                          onClick={() => {
                            setAttachTarget(t);
                            setAttachForm({ invoice_url: "", invoice_name: "" });
                          }}
                          title="Attach invoice or receipt"
                        >
                          <Paperclip size={12} />
                          <span>Attach</span>
                        </button>
                      )}
                    </td>
                    <td className="text-right debit-col">
                      {!isCredit ? money(t.amount) : "—"}
                    </td>
                    <td className="text-right credit-col">
                      {isCredit ? money(t.amount) : "—"}
                    </td>
                    <td className={`text-right running-col ${t.running_balance >= 0 ? "profit-text" : "loss-text"}`}>
                      {money(t.running_balance)}
                    </td>
                    <td className="text-right">
                      <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "flex-end" }}>
                        <button
                          className="icon-btn-micro edit"
                          onClick={() => openEditTx(t)}
                          title="Edit transaction entry"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          className="icon-btn-micro delete"
                          onClick={() => deleteTransaction(t.id)}
                          title="Delete transaction entry"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {txsWithRunning.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center empty-cell">
                    No transactions match your selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Transaction Modal (User specified fields) */}
      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Add Transaction</h2>
              <button className="icon-btn" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleAddTransaction}>
              <label>Service</label>
              <input
                required
                type="text"
                placeholder="e.g. Instagram, Canva, YouTube, Freelance Editor, Gear"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
              />

              <label>Name / Description</label>
              <input
                required
                type="text"
                placeholder="e.g. Brand Collaboration / Pro Subscription / Sponsorship"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label>Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="Credit">Credit (Income)</option>
                    <option value="Debit">Debit (Expense)</option>
                  </select>
                </div>
                <div>
                  <label>Amount (₹)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    placeholder="12000"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
              </div>

              <label>Date</label>
              <input
                required
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />

              <label>Notes (Optional)</label>
              <input
                type="text"
                placeholder="Additional invoice notes, reference IDs…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />

              {/* Attach Invoice Section */}
              <div style={{ marginTop: "14px" }}>
                <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Attach Invoice / Receipt (Optional)</span>
                  <small style={{ color: "#94a3b8", fontWeight: "normal" }}>PDF, PNG, JPG</small>
                </label>

                {form.invoice_url ? (
                  <div className="uploaded-file-chip">
                    <div className="uploaded-file-info">
                      <FileText size={15} />
                      <span className="uploaded-filename">{form.invoice_name || "Invoice attached"}</span>
                    </div>
                    <button
                      type="button"
                      className="icon-btn-micro remove"
                      onClick={() => setForm({ ...form, invoice_url: "", invoice_name: "" })}
                      title="Remove invoice"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <label className="invoice-upload-box">
                    <input
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/webp,image/jpg"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadInvoiceFile(f, true);
                      }}
                    />
                    <div className="upload-box-content">
                      <UploadCloud size={20} color="var(--blue, #3b82f6)" />
                      {uploadingInvoice ? (
                        <span>Uploading invoice document…</span>
                      ) : (
                        <>
                          <strong>Click to select invoice file</strong>
                          <span>or drag and drop invoice PDF / receipt here</span>
                        </>
                      )}
                    </div>
                  </label>
                )}

                {!form.invoice_url && (
                  <div style={{ marginTop: "8px" }}>
                    <div style={{ textAlign: "center", margin: "8px 0 6px", color: "#94a3b8", fontSize: "10px", fontWeight: 600 }}>
                      ── OR PASTE DOCUMENT LINK ──
                    </div>
                    <input
                      type="text"
                      placeholder="Paste external invoice link (Google Drive, Notion…)"
                      value={form.invoice_url}
                      onChange={(e) => setForm({ ...form, invoice_url: e.target.value, invoice_name: form.invoice_name || "Invoice Link" })}
                      style={{ fontSize: "12px", padding: "6px 10px" }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="primary-btn compact" disabled={uploadingInvoice}>Record Transaction</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {editingTx && (
        <div className="modal-backdrop" onClick={() => setEditingTx(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Edit Transaction Log</h2>
              <button className="icon-btn" onClick={() => setEditingTx(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleUpdateTransaction}>
              <label>Service</label>
              <input
                required
                type="text"
                placeholder="e.g. Instagram, Canva, YouTube, Freelance Editor, Gear"
                value={editForm.service}
                onChange={(e) => setEditForm({ ...editForm, service: e.target.value })}
              />

              <label>Name / Description</label>
              <input
                required
                type="text"
                placeholder="e.g. Brand Collaboration / Pro Subscription / Sponsorship"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label>Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  >
                    <option value="Credit">Credit (Income)</option>
                    <option value="Debit">Debit (Expense)</option>
                  </select>
                </div>
                <div>
                  <label>Amount (₹)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    placeholder="12000"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  />
                </div>
              </div>

              <label>Date</label>
              <input
                required
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />

              <label>Notes (Optional)</label>
              <input
                type="text"
                placeholder="Additional invoice notes, reference IDs…"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />

              {/* Attach Invoice Section */}
              <div style={{ marginTop: "14px" }}>
                <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Invoice / Receipt Document</span>
                  <small style={{ color: "#94a3b8", fontWeight: "normal" }}>PDF, PNG, JPG</small>
                </label>

                {editForm.invoice_url ? (
                  <div className="uploaded-file-chip">
                    <div className="uploaded-file-info">
                      <FileText size={15} />
                      <span className="uploaded-filename">{editForm.invoice_name || "Invoice attached"}</span>
                    </div>
                    <button
                      type="button"
                      className="icon-btn-micro remove"
                      onClick={() => setEditForm({ ...editForm, invoice_url: "", invoice_name: "" })}
                      title="Remove invoice"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <label className="invoice-upload-box">
                    <input
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/webp,image/jpg"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadInvoiceFile(f, "edit");
                      }}
                    />
                    <div className="upload-box-content">
                      <UploadCloud size={20} color="var(--blue, #3b82f6)" />
                      {uploadingInvoice ? (
                        <span>Uploading invoice document…</span>
                      ) : (
                        <>
                          <strong>Click to select invoice file</strong>
                          <span>or drag and drop invoice PDF / receipt here</span>
                        </>
                      )}
                    </div>
                  </label>
                )}

                {!editForm.invoice_url && (
                  <div style={{ marginTop: "8px" }}>
                    <div style={{ textAlign: "center", margin: "8px 0 6px", color: "#94a3b8", fontSize: "10px", fontWeight: 600 }}>
                      ── OR PASTE DOCUMENT LINK ──
                    </div>
                    <input
                      type="text"
                      placeholder="Paste external invoice link (Google Drive, Notion…)"
                      value={editForm.invoice_url}
                      onChange={(e) => setEditForm({ ...editForm, invoice_url: e.target.value, invoice_name: editForm.invoice_name || "Invoice Link" })}
                      style={{ fontSize: "12px", padding: "6px 10px" }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setEditingTx(null)}>Cancel</button>
                <button type="submit" className="primary-btn compact" disabled={uploadingInvoice}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal to Attach/Edit Invoice on Existing Transaction */}
      {attachTarget && (
        <div className="modal-backdrop" onClick={() => setAttachTarget(null)}>
          <div className="modal" style={{ maxWidth: "480px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Attach Invoice</h2>
                <p className="muted" style={{ fontSize: "12px", marginTop: "2px" }}>
                  {attachTarget.service} • {attachTarget.name} ({attachTarget.date})
                </p>
              </div>
              <button className="icon-btn" onClick={() => setAttachTarget(null)}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveInvoiceToTransaction} noValidate style={{ marginTop: "16px" }}>
              <div style={{ padding: "12px 14px", background: "#f8fafc", borderRadius: "6px", marginBottom: "16px", fontSize: "13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ color: "#64748b" }}>Amount:</span>
                  <strong className={attachTarget.type === "Credit" ? "credit-text" : "debit-text"}>
                    {attachTarget.type === "Credit" ? "+" : "-"}{money(attachTarget.amount)}
                  </strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b" }}>Type:</span>
                  <span>{attachTarget.type}</span>
                </div>
              </div>

              {attachForm.invoice_url ? (
                <div>
                  <label style={{ display: "block", marginBottom: "6px" }}>Attached Invoice Document</label>
                  <div className="uploaded-file-chip" style={{ marginBottom: "14px" }}>
                    <div className="uploaded-file-info">
                      <FileText size={16} />
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span className="uploaded-filename" style={{ fontWeight: 600 }}>
                          {attachForm.invoice_name || "Invoice Document"}
                        </span>
                        {attachForm.invoice_url.startsWith("http") && !attachForm.invoice_url.includes("/api/") ? (
                          <small style={{ color: "#64748b", fontSize: "11px", wordBreak: "break-all" }}>
                            {attachForm.invoice_url}
                          </small>
                        ) : null}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="icon-btn-micro remove"
                      onClick={() => setAttachForm({ invoice_url: "", invoice_name: "" })}
                      title="Remove or replace file"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Upload Invoice Document</span>
                    <small style={{ color: "#94a3b8" }}>PDF, PNG, JPG (Max 15MB)</small>
                  </label>

                  <label className="invoice-upload-box">
                    <input
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/webp,image/jpg"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadInvoiceFile(f, false);
                      }}
                    />
                    <div className="upload-box-content">
                      <UploadCloud size={20} color="var(--blue, #3b82f6)" />
                      {uploadingInvoice ? (
                        <span>Uploading invoice document…</span>
                      ) : (
                        <>
                          <strong>Click to select file</strong>
                          <span>or drag and drop invoice PDF or receipt</span>
                        </>
                      )}
                    </div>
                  </label>

                  <div style={{ textAlign: "center", margin: "14px 0 10px", color: "#94a3b8", fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px" }}>
                    ── OR PASTE DOCUMENT LINK ──
                  </div>

                  <label style={{ marginTop: "6px" }}>External Invoice Link / Document URL</label>
                  <input
                    type="text"
                    placeholder="https://drive.google.com/... or Notion URL"
                    value={attachForm.invoice_url}
                    onChange={(e) => setAttachForm({ ...attachForm, invoice_url: e.target.value })}
                  />
                </>
              )}

              <label style={{ marginTop: "12px" }}>Invoice Label / Name</label>
              <input
                type="text"
                placeholder="e.g. Invoice #1042 or Brand_Contract.pdf"
                value={attachForm.invoice_name}
                onChange={(e) => setAttachForm({ ...attachForm, invoice_name: e.target.value })}
              />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px" }}>
                {attachTarget.invoice_url ? (
                  <button
                    type="button"
                    className="text-btn"
                    style={{ color: "#dc2626" }}
                    onClick={() => handleRemoveInvoiceFromTransaction(attachTarget.id)}
                  >
                    Detach Invoice
                  </button>
                ) : <div />}

                <div style={{ display: "flex", gap: "10px" }}>
                  <button type="button" className="text-btn" onClick={() => setAttachTarget(null)}>Cancel</button>
                  <button type="submit" className="primary-btn compact" disabled={uploadingInvoice || !attachForm.invoice_url}>
                    Save Invoice
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Download Transaction History Modal */}
      {showExport && (
        <div className="modal-backdrop" onClick={() => setShowExport(false)}>
          <div className="modal export-statement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Download Transaction History</h2>
                <p className="muted" style={{ fontSize: "12px", marginTop: "2px" }}>
                  Export chronological transaction ledger statement with custom date range
                </p>
              </div>
              <button className="icon-btn" onClick={() => setShowExport(false)}><X size={18} /></button>
            </div>

            <div>
              <label>Select Date Range Preset</label>
              <div className="export-preset-pills">
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "all" ? "active" : ""}`}
                  onClick={() => handleExportPresetChange("all")}
                >
                  All Time
                </button>
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "this_month" ? "active" : ""}`}
                  onClick={() => handleExportPresetChange("this_month")}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "last_month" ? "active" : ""}`}
                  onClick={() => handleExportPresetChange("last_month")}
                >
                  Last Month
                </button>
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "last_3_months" ? "active" : ""}`}
                  onClick={() => handleExportPresetChange("last_3_months")}
                >
                  Last 3 Months
                </button>
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "ytd" ? "active" : ""}`}
                  onClick={() => handleExportPresetChange("ytd")}
                >
                  Year to Date
                </button>
                <button
                  type="button"
                  className={`export-preset-pill ${exportPreset === "custom" ? "active" : ""}`}
                  onClick={() => setExportPreset("custom")}
                >
                  Custom Range
                </button>
              </div>

              <div className="export-date-grid">
                <div>
                  <label>From Date</label>
                  <input
                    type="date"
                    value={exportStart}
                    onChange={(e) => {
                      setExportStart(e.target.value);
                      setExportPreset("custom");
                    }}
                  />
                </div>
                <div>
                  <label>To Date</label>
                  <input
                    type="date"
                    value={exportEnd}
                    onChange={(e) => {
                      setExportEnd(e.target.value);
                      setExportPreset("custom");
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label>Filter By Type</label>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <button
                    type="button"
                    className={`filter-pill ${exportType === "all" ? "active" : ""}`}
                    onClick={() => setExportType("all")}
                  >
                    All Transactions
                  </button>
                  <button
                    type="button"
                    className={`filter-pill ${exportType === "Credit" ? "active" : ""}`}
                    onClick={() => setExportType("Credit")}
                  >
                    Income (Credit)
                  </button>
                  <button
                    type="button"
                    className={`filter-pill ${exportType === "Debit" ? "active" : ""}`}
                    onClick={() => setExportType("Debit")}
                  >
                    Expenses (Debit)
                  </button>
                </div>
              </div>

              {/* Summary of matching data */}
              <div className="export-summary-box">
                <div className="export-summary-box-head">
                  <span>Statement Preview</span>
                  <span style={{ color: "var(--blue)", fontWeight: "600" }}>
                    {exportFilteredRecords.length} Transactions
                  </span>
                </div>
                <div className="export-stats-row">
                  <div className="export-stat-item">
                    <small>Total Income</small>
                    <strong className="credit-text">{money(exportTotalCredit)}</strong>
                  </div>
                  <div className="export-stat-item">
                    <small>Total Expenses</small>
                    <strong className="debit-text">{money(exportTotalDebit)}</strong>
                  </div>
                  <div className="export-stat-item">
                    <small>Net Margin</small>
                    <strong className={exportNetBalance >= 0 ? "profit-text" : "loss-text"}>
                      {money(exportNetBalance)}
                    </strong>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setShowExport(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={triggerExportDownload}
                  disabled={exportFilteredRecords.length === 0}
                >
                  <Download size={15} /> Download CSV Statement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   7. CONTENT LIBRARY (Color Palette — image, B rolls link, Content page links)
   ========================================================================== */
export function ContentLibraryView({ user, token }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("palette"); // palette | broll | page_link
  const [showAdd, setShowAdd] = useState(false);
  const [copiedHex, setCopiedHex] = useState("");
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    kind: "palette",
    url: "",
    color_hex: "#6366F1",
    category: "",
    notes: ""
  });

  const loadItems = async () => {
    try {
      setLoading(true);
      const r = await api(token).get("/library/items");
      setItems(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await api(token).post("/library/items", { ...form, kind: tab });
      setItems((prev) => [...prev, r.data]);
      setShowAdd(false);
      setForm({ title: "", kind: tab, url: "", color_hex: "#6366F1", category: "", notes: "" });
    } catch (e) {
      alert("Error adding item");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Remove this item from your library?")) return;
    try {
      await api(token).delete(`/library/items/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      alert("Error removing item");
    }
  };

  const copyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopiedHex(hex);
    setTimeout(() => setCopiedHex(""), 1800);
  };

  const palettes = items.filter(x => x.kind === "palette");
  const arolls = items.filter(x => x.kind === "aroll");
  const brolls = items.filter(x => x.kind === "broll");
  const pageLinks = items.filter(x => x.kind === "page_link");

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">BRAND & PRODUCTION ASSETS</p>
          <h1>Content Library</h1>
          <p className="muted">Color palette guidelines, A-roll primary recordings, B-roll cloud archives, and official content channel links.</p>
        </div>
        <button className="primary-btn compact" onClick={() => { setForm({ ...form, kind: tab }); setShowAdd(true); }}>
          <Plus size={16} /> Add {tab === "palette" ? "Color" : tab === "aroll" ? "A-Roll Link" : tab === "broll" ? "B-Roll Link" : "Page Link"}
        </button>
      </div>

      {/* 4 Main Navigation Tabs */}
      <div className="library-tabs-strip">
        <button className={tab === "palette" ? "lib-tab active" : "lib-tab"} onClick={() => setTab("palette")}>
          <Palette size={16} /> Color Palette — image & swatches ({palettes.length})
        </button>
        <button className={tab === "aroll" ? "lib-tab active" : "lib-tab"} onClick={() => setTab("aroll")}>
          <Video size={16} /> A rolls link ({arolls.length})
        </button>
        <button className={tab === "broll" ? "lib-tab active" : "lib-tab"} onClick={() => setTab("broll")}>
          <Film size={16} /> B rolls link ({brolls.length})
        </button>
        <button className={tab === "page_link" ? "lib-tab active" : "lib-tab"} onClick={() => setTab("page_link")}>
          <Share2 size={16} /> Content page links ({pageLinks.length})
        </button>
      </div>

      {/* Sub-view 1: Color Palette */}
      {tab === "palette" && (
        <div className="palette-view-wrap">
          {/* Brand Palette Visual Banner */}
          <div className="palette-showcase-banner">
            <div className="palette-visual-gradient">
              <div className="swatch-strip">
                {palettes.slice(0, 6).map((p) => (
                  <div key={p.id} className="swatch-stripe" style={{ background: p.color_hex }} />
                ))}
              </div>
            </div>
            <div className="palette-banner-info">
              <span className="eyebrow">STUDIO VISUAL IDENTITY</span>
              <h2>Creator Brand Color Palette</h2>
              <p>Consistent visual grading palette used across thumbnails, motion graphics, and channel branding.</p>
            </div>
          </div>

          {/* Color Swatch Cards Grid */}
          <div className="palette-swatches-grid">
            {palettes.map((c) => (
              <div key={c.id} className="swatch-card" onClick={() => copyColor(c.color_hex)}>
                <div className="swatch-color-box" style={{ background: c.color_hex }}>
                  <button className="copy-hex-float">
                    {copiedHex === c.color_hex ? <Check size={14} className="green-text" /> : <Copy size={14} />}
                  </button>
                </div>
                <div className="swatch-meta">
                  <b>{c.title}</b>
                  <div className="swatch-code-row">
                    <span className="hex-tag">{c.color_hex}</span>
                    <small>{c.category || "Accent"}</small>
                  </div>
                  {c.notes && <p className="swatch-notes">{c.notes}</p>}
                </div>
                <button
                  className="card-del-btn"
                  onClick={(e) => { e.stopPropagation(); deleteItem(c.id); }}
                  title="Remove color"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-view 2: A Rolls Link */}
      {tab === "aroll" && (
        <div className="broll-view-wrap">
          <div className="broll-grid">
            {arolls.map((a) => (
              <div key={a.id} className="broll-card">
                <div className="broll-icon-wrap" style={{ background: "rgba(99, 102, 241, 0.12)", color: "#6366f1" }}>
                  <Video size={22} />
                </div>
                <div className="broll-info">
                  <span className="broll-category" style={{ background: "rgba(99, 102, 241, 0.1)", color: "#6366f1" }}>{a.category || "A-Roll Footage"}</span>
                  <h3>{a.title}</h3>
                  {a.notes && <p className="broll-notes">{a.notes}</p>}
                </div>
                <div className="broll-actions">
                  <a
                    href={a.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="open-link-btn"
                  >
                    Open Drive Folder <ExternalLink size={14} />
                  </a>
                  <button className="card-del-btn" onClick={() => deleteItem(a.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {arolls.length === 0 && (
            <div className="empty-state">
              <Video size={32} />
              <h3>No A-roll links yet</h3>
              <p>Add links to your talking-head camera recordings, primary audio tracks, and raw multi-cam shoot footage.</p>
            </div>
          )}
        </div>
      )}

      {/* Sub-view 3: B Rolls Link */}
      {tab === "broll" && (
        <div className="broll-view-wrap">
          <div className="broll-grid">
            {brolls.map((b) => (
              <div key={b.id} className="broll-card">
                <div className="broll-icon-wrap">
                  <Film size={22} />
                </div>
                <div className="broll-info">
                  <span className="broll-category">{b.category || "B-Roll Pack"}</span>
                  <h3>{b.title}</h3>
                  {b.notes && <p className="broll-notes">{b.notes}</p>}
                </div>
                <div className="broll-actions">
                  <a
                    href={b.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="open-link-btn"
                  >
                    Open Drive Folder <ExternalLink size={14} />
                  </a>
                  <button className="card-del-btn" onClick={() => deleteItem(b.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {brolls.length === 0 && (
            <div className="empty-state">
              <Film size={32} />
              <h3>No B-roll links yet</h3>
              <p>Add links to your cloud storage folders, Google Drive footage packs, and studio archives.</p>
            </div>
          )}
        </div>
      )}

      {/* Sub-view 4: Content Page Links */}
      {tab === "page_link" && (
        <div className="pagelinks-view-wrap">
          <div className="pagelinks-grid">
            {pageLinks.map((pl) => (
              <div key={pl.id} className="pagelink-card">
                <div className="pagelink-top">
                  <span className="platform-tag">{pl.category || "Channel"}</span>
                  <button className="card-del-btn" onClick={() => deleteItem(pl.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
                <h3>{pl.title}</h3>
                {pl.notes && <p className="pagelink-notes">{pl.notes}</p>}
                <a
                  href={pl.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="visit-page-btn"
                >
                  Visit Channel / Page <ExternalLink size={14} />
                </a>
              </div>
            ))}
          </div>

          {pageLinks.length === 0 && (
            <div className="empty-state">
              <Share2 size={32} />
              <h3>No content page links yet</h3>
              <p>Bookmark your YouTube channel, Instagram creator page, Notion database, and media kit.</p>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Add to {tab === "palette" ? "Color Palette" : tab === "aroll" ? "A-Roll Links" : tab === "broll" ? "B-Roll Links" : "Content Page Links"}</h2>
              <button className="icon-btn" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <label>Title / Name</label>
              <input
                required
                type="text"
                placeholder={
                  tab === "palette"
                    ? "e.g. Electric Violet"
                    : tab === "aroll"
                      ? "e.g. Talking Head A-Cam Raw Footage"
                      : tab === "broll"
                        ? "e.g. 4K Drone Skyline Footage"
                        : "e.g. YouTube Official Channel"
                }
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />

              {tab === "palette" ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <label>Hex Color Code</label>
                      <input
                        type="text"
                        placeholder="#6366F1"
                        value={form.color_hex}
                        onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                      />
                    </div>
                    <div>
                      <label>Color Picker</label>
                      <input
                        type="color"
                        style={{ height: "44px", padding: "4px" }}
                        value={form.color_hex}
                        onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                      />
                    </div>
                  </div>
                  <label>Usage Category</label>
                  <input
                    type="text"
                    placeholder="e.g. Primary Brand / Thumbnail Accent"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  />
                </>
              ) : (
                <>
                  <label>Destination URL</label>
                  <input
                    required
                    type="url"
                    placeholder="https://drive.google.com/... or https://youtube.com/..."
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                  />
                  <label>Category Tag</label>
                  <input
                    type="text"
                    placeholder={
                      tab === "aroll"
                        ? "e.g. Studio A-Cam, Podcast Multi-Cam, Dialogue Master"
                        : tab === "broll"
                          ? "e.g. Studio Cutaways, Drone, Screen captures"
                          : "e.g. Social, Notion Workspace, Media Kit"
                    }
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  />
                </>
              )}

              <label>Description / Usage Notes</label>
              <input
                type="text"
                placeholder="Brief guidelines or instructions…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" className="text-btn" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="primary-btn compact">Save Item</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   8. TEAM MANAGEMENT & GRANULAR ACCESS CONTROL
   ========================================================================== */
export const AVAILABLE_MODULES = [
  { id: "dashboard", label: "Dashboard", desc: "Studio metrics, overview & quick actions" },
  { id: "scripts", label: "Scripts", desc: "Script writing, drafting & tag filtering" },
  { id: "production", label: "Production", desc: "Video production pipeline & editing stages" },
  { id: "calendar", label: "Content Calendar", desc: "Content schedule & publication calendar" },
  { id: "deals", label: "Brand Deals", desc: "Brand partnerships, sponsorships & pipeline" },
  { id: "finances", label: "Finances", desc: "Income, expense ledger & auto profit/loss" },
  { id: "library", label: "Content Library", desc: "Color palettes, A-Roll & B-Roll archives" },
  { id: "team", label: "Team Management", desc: "Manage workspace members & permissions" },
];

export const ROLE_PRESET_PERMS = {
  admin: ["dashboard", "scripts", "production", "calendar", "deals", "finances", "library", "team"],
  manager: ["dashboard", "scripts", "production", "calendar", "deals", "finances", "library"],
  script_writer: ["scripts", "calendar", "library", "production"],
  editor: ["production", "calendar", "library", "scripts"],
};

export function TeamView({ user, token }) {
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "contenthq",
    role: "editor",
    permissions: ["production", "calendar", "library"],
  });

  const loadTeam = async () => {
    try {
      setLoading(true);
      const res = await api(token).get("/team");
      setMembers(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load team members. " + (err.response?.data?.detail || ""));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeam();
  }, []);

  const openAddModal = () => {
    setEditingMember(null);
    setForm({
      name: "",
      email: "",
      password: "contenthq",
      role: "editor",
      permissions: ["dashboard", "production", "library"],
    });
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setForm({
      name: member.name,
      email: member.email,
      password: "",
      role: member.role || "editor",
      permissions: member.permissions || ROLE_PRESET_PERMS[member.role] || [],
    });
    setShowModal(true);
  };

  const handleRoleChange = (newRole) => {
    const defaultPerms = ROLE_PRESET_PERMS[newRole] || form.permissions;
    setForm({
      ...form,
      role: newRole,
      permissions: defaultPerms,
    });
  };

  const togglePermission = (modId) => {
    setForm((prev) => {
      const exists = prev.permissions.includes(modId);
      const next = exists ? prev.permissions.filter((p) => p !== modId) : [...prev.permissions, modId];
      return { ...prev, permissions: next };
    });
  };

  const selectAllPermissions = () => {
    setForm((prev) => ({ ...prev, permissions: AVAILABLE_MODULES.map((m) => m.id) }));
  };

  const clearAllPermissions = () => {
    setForm((prev) => ({ ...prev, permissions: [] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      if (editingMember) {
        const patchData = {
          name: form.name.trim(),
          role: form.role,
          permissions: form.permissions,
        };
        if (form.password.trim()) {
          patchData.password = form.password.trim();
        }
        await api(token).patch(`/team/${editingMember.id}`, patchData);
      } else {
        await api(token).post("/team", {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password.trim() || "contenthq",
          role: form.role,
          permissions: form.permissions,
        });
      }
      setShowModal(false);
      loadTeam();
    } catch (err) {
      alert(err.response?.data?.detail || "Error saving team member");
    }
  };

  const handleDelete = async (memberId, memberName) => {
    if (memberId === user.id) {
      alert("You cannot remove your own account.");
      return;
    }
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the workspace?`)) return;
    try {
      await api(token).delete(`/team/${memberId}`);
      loadTeam();
    } catch (err) {
      alert(err.response?.data?.detail || "Error removing team member");
    }
  };

  // Metrics
  const adminCount = members.filter((m) => m.role === "admin").length;
  const managerCount = members.filter((m) => m.role === "manager").length;
  const writerCount = members.filter((m) => m.role === "script_writer").length;
  const editorCount = members.filter((m) => m.role === "editor").length;

  return (
    <div className="workspace-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">WORKSPACE & ACCESS CONTROL</p>
          <h1>Team Management</h1>
          <p className="muted">
            Manage team members, roles, and grant customized access permissions to workspace modules.
          </p>
        </div>
        {isAdmin && (
          <button className="primary-btn compact" onClick={openAddModal}>
            <Plus size={16} /> Add Team Member
          </button>
        )}
      </div>

      {error && <ErrorBox message={error} />}

      {/* Summary Cards */}
      <div className="team-summary-grid">
        <div className="team-stat-card">
          <small>Total Members</small>
          <strong>{members.length}</strong>
          <span>Active in workspace</span>
        </div>
        <div className="team-stat-card">
          <small>Admins / Owners</small>
          <strong style={{ color: "#4f46e5" }}>{adminCount}</strong>
          <span>Full unrestricted access</span>
        </div>
        <div className="team-stat-card">
          <small>Managers</small>
          <strong style={{ color: "#7e22ce" }}>{managerCount}</strong>
          <span>Deals & operations</span>
        </div>
        <div className="team-stat-card">
          <small>Script Writers</small>
          <strong style={{ color: "#15803d" }}>{writerCount}</strong>
          <span>Scriptwriting & research</span>
        </div>
        <div className="team-stat-card">
          <small>Editors</small>
          <strong style={{ color: "#0284c7" }}>{editorCount}</strong>
          <span>Video editing & cutaways</span>
        </div>
      </div>

      {/* Team Members List */}
      <div className="panel" style={{ marginTop: "24px" }}>
        <div className="panel-head">
          <div>
            <p className="eyebrow">ACTIVE DIRECTORY</p>
            <h2>Workspace Members & Permissions</h2>
          </div>
          <span className="muted" style={{ fontSize: "13px" }}>
            {members.length} members registered
          </span>
        </div>

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading team directory…</div>
        ) : members.length === 0 ? (
          <div className="empty-state">
            <Users size={32} />
            <h3>No team members found</h3>
            <p>Click "Add Team Member" above to invite collaborators.</p>
          </div>
        ) : (
          <div className="team-members-grid">
            {members.map((m) => {
              const perms = m.permissions || ROLE_PRESET_PERMS[m.role] || [];
              const isSelf = m.id === user?.id;

              return (
                <div key={m.id} className="team-member-card">
                  <div className="team-member-header">
                    <div className="member-avatar-col">
                      <span className="member-avatar">
                        {(m.name || "U")[0].toUpperCase()}
                      </span>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <h3>{m.name}</h3>
                          {isSelf && <span className="self-badge">You</span>}
                        </div>
                        <p className="member-email">{m.email}</p>
                      </div>
                    </div>

                    <span className={`team-role-badge role-${m.role}`}>
                      {m.role_label || roleLabel[m.role] || m.role}
                    </span>
                  </div>

                  {/* Access Permissions Section */}
                  <div className="member-perms-section">
                    <div className="perms-title-row">
                      <Shield size={14} />
                      <span>Allowed Access Modules ({perms.length})</span>
                    </div>
                    <div className="team-perms-list">
                      {perms.map((pid) => {
                        const mod = AVAILABLE_MODULES.find((x) => x.id === pid);
                        return (
                          <span key={pid} className="perm-chip" title={mod?.desc || pid}>
                            <Check size={11} className="perm-check-icon" />
                            {mod?.label || pid}
                          </span>
                        );
                      })}
                      {perms.length === 0 && (
                        <span className="muted" style={{ fontSize: "12px", fontStyle: "italic" }}>
                          No modules assigned
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Footer Actions */}
                  {isAdmin && (
                    <div className="member-card-footer">
                      <button
                        className="text-btn compact"
                        onClick={() => openEditModal(m)}
                        title="Change role or customized permissions"
                      >
                        <Edit2 size={13} /> Edit Access
                      </button>
                      {!isSelf && (
                        <button
                          className="danger-btn compact"
                          onClick={() => handleDelete(m.id, m.name)}
                          title="Remove user from team"
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Member Modal */}
      {/* Add / Edit Member Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <p className="eyebrow" style={{ margin: 0 }}>
                  {editingMember ? "EDIT MEMBER ACCESS" : "INVITE COLLABORATOR"}
                </p>
                <h2 style={{ margin: "4px 0 0", fontSize: "20px" }}>
                  {editingMember ? `Edit ${editingMember.name}` : "Add New Team Member"}
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn modal-close-btn"
                onClick={() => setShowModal(false)}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label>Full Name</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label>Email Address</label>
                  <input
                    required
                    type="email"
                    placeholder="rahul@creator.studio"
                    value={form.email}
                    disabled={!!editingMember}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "10px" }}>
                <div>
                  <label>Designated Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                  >
                    <option value="admin">Admin / Owner (Full access)</option>
                    <option value="manager">Manager (Operations & Deals)</option>
                    <option value="script_writer">Script Writer</option>
                    <option value="editor">Video Editor</option>
                  </select>
                </div>
                <div>
                  <label>{editingMember ? "New Password (optional)" : "Initial Password"}</label>
                  <input
                    type="password"
                    placeholder={editingMember ? "Leave blank to keep current" : "Default: contenthq"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>

              {/* Granular Module Access Selection */}
              <div className="permissions-selector-block">
                <div className="perms-selector-header">
                  <div>
                    <label style={{ margin: 0, fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
                      Choose Module Access Permissions
                    </label>
                    <p className="muted" style={{ fontSize: "12px", margin: "2px 0 0" }}>
                      Admin can check or uncheck which sections this member is authorized to access.
                    </p>
                  </div>
                  <div className="quick-select-btns">
                    <button type="button" onClick={selectAllPermissions}>
                      Select All
                    </button>
                    <span className="dot-sep">·</span>
                    <button type="button" onClick={clearAllPermissions}>
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="permissions-picker-grid">
                  {AVAILABLE_MODULES.map((mod) => {
                    const isChecked = form.permissions.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        className={`perm-picker-item ${isChecked ? "checked" : ""}`}
                        onClick={() => togglePermission(mod.id)}
                      >
                        <div className="perm-picker-checkbox">
                          {isChecked ? <CheckSquare size={17} className="checked-icon" /> : <Square size={17} />}
                        </div>
                        <div className="perm-picker-info">
                          <b>{mod.label}</b>
                          <span>{mod.desc}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="modal-actions-footer">
                <button type="button" className="text-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn compact" style={{ padding: "9px 20px" }}>
                  {editingMember ? "Update Permissions" : "Create & Grant Access"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Fallback stubs for legacy route compatibility if referenced
export function ListView() { return <div className="workspace-page">Overview</div>; }
export function DetailView() { return <div className="workspace-page">Detail</div>; }
export function FinanceView(props) { return <FinancesView {...props} />; }
export function Analytics(props) { return <Dashboard {...props} />; }
export function SettingsView() { return <div className="workspace-page">Settings</div>; }
